import React, { useState, useEffect, useRef, useMemo } from 'react';
import { analyticsAPI } from '../services/api';
import ReportScheduler from './ReportScheduler';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

// Wellsync-inspired color palette - mature corporate healthcare aesthetic
const COLORS = {
  primary: '#1e40af',      // Deep corporate blue
  secondary: '#3b82f6',    // Bright blue
  accent: '#0891b2',       // Teal accent
  highlight: '#06b6d4',    // Cyan
  success: '#059669',      // Emerald green (deeper)
  warning: '#d97706',      // Amber (deeper)
  danger: '#dc2626',       // Red
  muted: '#64748b',        // Slate gray
  light: '#f8fafc',        // Very light slate
  white: '#ffffff',
  border: '#e2e8f0',       // Slate border
  dark: '#334155',         // Slate dark
  // Chart colors - professional, corporate palette
  chartColors: ['#1e40af', '#059669', '#0891b2', '#7c3aed', '#db2777', '#ea580c', '#0d9488', '#4f46e5'],
  // Gradient backgrounds for feature cards
  gradientStart: '#1e40af',
  gradientEnd: '#3b82f6',
  // Wellsync-style gradients
  cardGradient: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
  lightGradient: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
};

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'uvc', label: 'UVC Analytics' },
  { id: 'operators', label: 'Operator Performance' },
  { id: 'wards', label: 'Ward/Bed Analysis' },
  { id: 'compliance', label: 'Compliance & Risk' },
  { id: 'killswitch', label: 'Emergency Stop KPIs' },
  { id: 'trends', label: 'Trends & Patterns' },
  { id: 'insights', label: 'Additional Insights' },
];

// Move CustomTooltip outside to prevent recreation on every render
const CustomTooltip = React.memo(({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const entry = payload[0];
    
    // Check if this is a pie chart with side/count structure (UV-C Position Distribution)
    if (entry.payload && entry.payload.side !== undefined) {
      return (
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-lg">
          <p className="text-gray-900 font-medium mb-2">{entry.payload.side}</p>
          <p className="text-sm">
            Count: <span className="text-black font-semibold">{entry.payload.count.toLocaleString()}</span>
          </p>
        </div>
      );
    }
    
    // Check if this is a pie chart with type/total_sessions structure (Sessions by Ward Type)
    if (entry.payload && entry.payload.type !== undefined && entry.payload.total_sessions !== undefined) {
      return (
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-lg">
          <p className="text-gray-900 font-medium mb-2">{entry.payload.type}</p>
          <p className="text-sm">
            Sessions: <span className="text-black font-semibold">{entry.payload.total_sessions.toLocaleString()}</span>
          </p>
        </div>
      );
    }
    
    // Default tooltip for other chart types
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-lg">
        <p className="text-gray-900 font-medium mb-2">{label}</p>
        {payload.map((item, index) => (
          <p key={index} style={{ color: item.color }} className="text-sm">
            {item.name}: {typeof item.value === 'number' ? item.value.toLocaleString() : item.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
});

// Wellsync-style KPI Card - mature corporate healthcare aesthetic with custom icons
export const KPICard = React.memo(({ title, value, subtitle, trend, icon, color = 'default', tooltip, featured = false }) => {
  const [showTooltip, setShowTooltip] = React.useState(false);
  
  // Icon mapping for different KPI types
  const getIcon = () => {
    const iconMap = {
      sessions: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      success: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      coverage: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      compliance: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      ),
      time: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      robot: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      operator: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
      error: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      ),
      default: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      ),
    };
    return iconMap[icon] || iconMap.default;
  };

  const colorStyles = {
    default: { bg: 'bg-white', iconBg: 'bg-slate-100', iconColor: 'text-slate-600', border: 'border-slate-200' },
    primary: { bg: 'bg-white', iconBg: 'bg-blue-100', iconColor: 'text-blue-600', border: 'border-blue-100' },
    success: { bg: 'bg-white', iconBg: 'bg-emerald-100', iconColor: 'text-emerald-600', border: 'border-emerald-100' },
    warning: { bg: 'bg-white', iconBg: 'bg-amber-100', iconColor: 'text-amber-600', border: 'border-amber-100' },
    danger: { bg: 'bg-white', iconBg: 'bg-red-100', iconColor: 'text-red-600', border: 'border-red-100' },
  };

  const style = colorStyles[color] || colorStyles.default;
  
  // Featured card style (like Wellsync's Vital Stats card)
  if (featured) {
    return (
      <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl p-6 text-white shadow-lg shadow-blue-500/20 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
        <div className="relative">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                {getIcon()}
              </div>
              <h3 className="text-sm font-semibold text-blue-100 uppercase tracking-wider">{title}</h3>
            </div>
            {trend !== undefined && (
              <div className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${
                trend >= 0 ? 'bg-emerald-400/20 text-emerald-200' : 'bg-red-400/20 text-red-200'
              }`}>
                <span>{trend >= 0 ? '\u25B2' : '\u25BC'}</span>
                <span>{Math.abs(trend)}%</span>
              </div>
            )}
          </div>
          <p className="text-4xl font-bold mb-2 tracking-tight">{value}</p>
          {subtitle && <p className="text-sm text-blue-200 font-medium">{subtitle}</p>}
        </div>
      </div>
    );
  }
  
  return (
    <div className={`${style.bg} rounded-2xl p-5 border ${style.border} shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden group`}>
      <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-slate-50 to-transparent rounded-bl-full opacity-50" />
      <div className="relative">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 ${style.iconBg} rounded-xl flex items-center justify-center ${style.iconColor} group-hover:scale-110 transition-transform duration-300`}>
              {getIcon()}
            </div>
            <div className="flex items-center gap-2">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{title}</h3>
              {tooltip && (
                <span 
                  className="cursor-pointer text-slate-400 hover:text-slate-600" 
                  title={tooltip}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </span>
              )}
            </div>
          </div>
          {trend !== undefined && (
            <div className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${
              trend >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
            }`}>
              <span>{trend >= 0 ? '\u25B2' : '\u25BC'}</span>
              <span>{Math.abs(trend)}%</span>
            </div>
          )}
        </div>
        <p className="text-3xl font-bold text-slate-800 mb-1 tracking-tight">{value}</p>
        {subtitle && <p className="text-sm text-slate-500 font-medium">{subtitle}</p>}
      </div>
    </div>
  );
});


// QlikSense-style Sortable & Filterable Table - professional data grid with column filters
export const SortableTable = ({ columns, data, defaultSortKey, defaultSortDir = 'desc', compact = false, title, onExport, showFilters = true, onRowClick }) => {
  const [sortKey, setSortKey] = useState(defaultSortKey || (columns[0]?.key || ''));
  const [sortDir, setSortDir] = useState(defaultSortDir);
  const [columnFilters, setColumnFilters] = useState({});
  const [showFilterRow, setShowFilterRow] = useState(false);

  // Filter data based on column filters
  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.filter(row => {
      return Object.entries(columnFilters).every(([key, filterValue]) => {
        if (!filterValue || filterValue === '') return true;
        const cellValue = String(row[key] ?? '').toLowerCase();
        return cellValue.includes(filterValue.toLowerCase());
      });
    });
  }, [data, columnFilters]);

  // Sort filtered data
  const sortedData = useMemo(() => {
    if (filteredData.length === 0) return [];
    return [...filteredData].sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
      }
      const aStr = String(aVal || '').toLowerCase();
      const bStr = String(bVal || '').toLowerCase();
      return sortDir === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });
  }, [filteredData, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const handleFilterChange = (key, value) => {
    setColumnFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearAllFilters = () => {
    setColumnFilters({});
  };

  const hasActiveFilters = Object.values(columnFilters).some(v => v && v !== '');

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      {/* Header with title, filter toggle, and export */}
      {(title || onExport || showFilters) && (
        <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gradient-to-r from-slate-50 to-white">
          <div className="flex items-center gap-3">
            {title && <h3 className="text-lg font-semibold text-gray-900">{title}</h3>}
            <span className="text-sm text-gray-500">({sortedData.length} of {data?.length || 0} records)</span>
          </div>
          <div className="flex items-center gap-2">
            {showFilters && (
              <>
                <button
                  onClick={() => setShowFilterRow(!showFilterRow)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${
                    showFilterRow ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                  Filter
                </button>
                {hasActiveFilters && (
                  <button
                    onClick={clearAllFilters}
                    className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 transition-colors"
                  >
                    Clear Filters
                  </button>
                )}
              </>
            )}
            {onExport && (
              <button
                onClick={() => onExport(sortedData, columns)}
                className="px-4 py-1.5 bg-gray-900 hover:bg-gray-800 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Export Data
              </button>
            )}
          </div>
        </div>
      )}
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            {/* Sort headers */}
            <tr className="bg-gradient-to-r from-slate-50 to-slate-100">
              {columns.map(col => (
                <th
                  key={col.key}
                  onClick={() => handleSort(col.key)}
                  className={`text-left ${compact ? 'py-2 px-3' : 'py-3 px-4'} text-xs font-bold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-200/50 transition-colors select-none border-b border-slate-200`}
                >
                  <div className="flex items-center gap-2">
                    {col.label}
                    <span className={`transition-opacity ${sortKey === col.key ? 'opacity-100' : 'opacity-30'}`}>
                      {sortKey === col.key ? (sortDir === 'asc' ? '\u25B2' : '\u25BC') : '\u25BC'}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
            {/* Filter row */}
            {showFilterRow && (
              <tr className="bg-blue-50/50">
                {columns.map(col => (
                  <th key={`filter-${col.key}`} className={`${compact ? 'py-1 px-3' : 'py-2 px-4'} border-b border-slate-200`}>
                    <input
                      type="text"
                      placeholder={`Filter ${col.label}...`}
                      value={columnFilters[col.key] || ''}
                      onChange={(e) => handleFilterChange(col.key, e.target.value)}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </th>
                ))}
              </tr>
            )}
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sortedData.map((row, i) => (
              <tr 
                key={i} 
                className={`bg-white hover:bg-blue-50/50 transition-colors ${onRowClick ? 'cursor-pointer' : ''}`}
                onClick={() => onRowClick && onRowClick(row)}
              >
                {columns.map(col => (
                  <td key={col.key} className={`${compact ? 'py-2 px-3' : 'py-3 px-4'} text-sm text-slate-700`}>
                    {col.render ? col.render(row[col.key], row) : (
                      typeof row[col.key] === 'number' 
                        ? row[col.key].toLocaleString() 
                        : String(row[col.key] ?? '-')
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {(!data || data.length === 0) && (
          <div className="text-center py-12 text-slate-400 bg-slate-50">
            <div className="text-4xl mb-2">📊</div>
            <div className="font-medium">No data available</div>
            <div className="text-xs mt-1">Try adjusting your filters</div>
          </div>
        )}
        {data && data.length > 0 && sortedData.length === 0 && (
          <div className="text-center py-12 text-slate-400 bg-slate-50">
            <div className="text-4xl mb-2">🔍</div>
            <div className="font-medium">No matching records</div>
            <div className="text-xs mt-1">Try adjusting your column filters</div>
          </div>
        )}
      </div>
    </div>
  );
};

const InsightsDashboard = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
    const [timeRange, setTimeRange] = useState(30);
    const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
    const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
    const [isTodaySelected, setIsTodaySelected] = useState(false);
  const [trends, setTrends] = useState([]);
  const [robots, setRobots] = useState({ robots: [], statistics: {} });
  const [operators, setOperators] = useState([]);
  const [hourly, setHourly] = useState([]);
  const [errors, setErrors] = useState({ errors: [], summary: {} });
  const [hospitals, setHospitals] = useState([]);
  const [availableRobots, setAvailableRobots] = useState([]);
    const [selectedRobot, setSelectedRobot] = useState(null);
    const [drillDown, setDrillDown] = useState(null);
    const [selectedHospital, setSelectedHospital] = useState(null);
    const [showReportScheduler, setShowReportScheduler] = useState(false);
    const [selectedOperator, setSelectedOperator] = useState(null);
    const [showExportModal, setShowExportModal] = useState(false);
    const [showFleetModal, setShowFleetModal] = useState(false);
    const [fleetSessions, setFleetSessions] = useState([]);
    const [selectedFleetRobot, setSelectedFleetRobot] = useState(null);
  const chartRef = useRef(null);
  
  // Request deduplication refs to prevent duplicate API calls
  const loadingAnalyticsRef = useRef(false);
  const loadingRobotsRef = useRef(false);
  const loadingTabDataRef = useRef(false);
  
  // Extended analytics state
  const [robotPerformance, setRobotPerformance] = useState({ robots: [], protocol_compliance: {}, side_distribution: [], version_comparison: [] });
  const [inactiveRobots, setInactiveRobots] = useState({ inactive_24h: [], inactive_48h: [], inactive_72h: [], inactive_72plus: [], summary: {} });
  const [uvcAnalytics, setUvcAnalytics] = useState({ overview: {}, by_hospital: [], daily_trend: [], side_distribution: [], duration_distribution: [] });
  const [uvcSessionsDetail, setUvcSessionsDetail] = useState({ sessions: [], count: 0, duration_range: null });
  const [showUvcDrillDown, setShowUvcDrillDown] = useState(false);
  const [uvcBulbUsage, setUvcBulbUsage] = useState({ bulb_usage: [], count: 0 });
  const [uvcBulbDetail, setUvcBulbDetail] = useState({ hospitals: [], count: 0 });
  const [selectedBulbHospital, setSelectedBulbHospital] = useState(null);
  const [selectedBulbRobot, setSelectedBulbRobot] = useState(null);
  const [bulbAvailableRobots, setBulbAvailableRobots] = useState([]);
  const [showUvcResetModal, setShowUvcResetModal] = useState(false);
  const [uvcResetForm, setUvcResetForm] = useState({ serial: '', reset_date: '', reset_time: '', notes: '' });
  const [uvcResetSaving, setUvcResetSaving] = useState(false);
  const [uvcResetHistory, setUvcResetHistory] = useState([]);
  const [operatorDetails, setOperatorDetails] = useState({ operators: [], error_details: [], shift_performance: [], role_performance: [] });
  const [wardBed, setWardBed] = useState({ wards: [], beds: [], ward_types: [], coverage: {} });
  const [additionalInsights, setAdditionalInsights] = useState({ duration_analysis: [], day_of_week: [], peak_hours: [], sync_health: {}, period_stats: {}, database_totals: {}, incomplete_cycles: { total_incomplete: 0, affected_robots: 0, affected_operators: 0, affected_hospitals: 0, details: [] } });
  const [killswitchKPIs, setKillswitchKPIs] = useState({
    total_all_time: 0,
    period_stops: 0,
    period_days: 30,
    emergency_stop_rate: 0,
    affected_robots: 0,
    total_sessions_period: 0,
    top_operators: [],
    top_robots: [],
    daily_trend: [],
    latest_events: [],
    by_hospital: []
  });
  const [emergencyStopSessions, setEmergencyStopSessions] = useState([]);
  const [selectedEmergencyHospital, setSelectedEmergencyHospital] = useState(null);
  const [showEmergencyModal, setShowEmergencyModal] = useState(false);
  const [performanceKPIs, setPerformanceKPIs] = useState({ 
    kpis: {}, 
    top_performers: { robots: [], operators: [] }, 
    trends: { weekly: [], monthly: [], error_rate: [] }, 
    week_over_week: {
      this_week_sessions: 0,
      last_week_sessions: 0,
      sessions_change: 0,
      sessions_change_percent: 0,
      this_week_success_rate: 0,
      last_week_success_rate: 0
    },
    efficiency: { shift_performance: [] },
    compliance: {}, 
    compliance_gaps: {},
    risk_indicators: { high_error_operators: [], high_error_robots: [] }
  });

  const userRole = user?.role || 'hospital_admin';
  const userHospitalId = user?.hospital_id;
  const isGroupLevel = userRole === 'super_admin' || userRole === 'Superadmin';
  const isSuperAdmin = userRole === 'super_admin' || userRole === 'Superadmin';
  const isRegionalLevel = userRole === 'company_admin' || userRole === 'regional' || userRole === 'Regional' || userRole === 'Regional ' || userRole === 'Regional Admin' || userRole === 'Admin';
  const isHospitalLevel = userRole === 'hospital_admin' || userRole === 'hospital_staff' || userRole === 'Ipp' || userRole === 'Operator';

      // Load hospitals once on mount — list must be stable and NOT change with the date filter.
      // Using a separate effect prevents the hospital dropdown from re-ordering or shrinking
      // when the date range changes (which would cause selectedHospital to point to the wrong entry).
      useEffect(() => {
        if (!(isGroupLevel || isRegionalLevel)) return;
        analyticsAPI.getHospitals()
          .then(res => setHospitals(res.data?.hospitals || []))
          .catch(() => setHospitals([]));
      }, [isGroupLevel, isRegionalLevel]);

        // Load available robots when hospital changes (with deduplication)
      useEffect(() => {
        const loadRobots = async () => {
          // Prevent duplicate concurrent calls
          if (loadingRobotsRef.current) return;
          loadingRobotsRef.current = true;
          
          try {
            const hospitalId = isHospitalLevel ? userHospitalId : selectedHospital;
            const startDate = customDateRange.start || null;
            const endDate = customDateRange.end || null;
            const res = await analyticsAPI.getRobots(hospitalId, timeRange, null, startDate, endDate);
            setAvailableRobots(res.data?.robots || []);
            setSelectedRobot(null); // Reset robot selection when hospital changes
          } catch (error) {
            console.error('Failed to load robots:', error);
            setAvailableRobots([]);
          } finally {
            loadingRobotsRef.current = false;
          }
        };
        loadRobots();
      }, [selectedHospital, userHospitalId, isHospitalLevel, timeRange, customDateRange]);

      // Initialize bulb hospital filter for hospital-level users
      useEffect(() => {
        if (isHospitalLevel && userHospitalId) {
          setSelectedBulbHospital(userHospitalId);
        }
      }, [isHospitalLevel, userHospitalId]);

      // Load robots for bulb section when bulb hospital changes
      useEffect(() => {
        const loadBulbRobots = async () => {
          try {
            const hospitalId = isHospitalLevel ? userHospitalId : selectedBulbHospital;
            if (!hospitalId) {
              setBulbAvailableRobots([]);
              return;
            }
            const startDate = customDateRange.start || null;
            const endDate = customDateRange.end || null;
            const res = await analyticsAPI.getRobots(hospitalId, timeRange, null, startDate, endDate);
            setBulbAvailableRobots(res.data?.robots || []);
            setSelectedBulbRobot(null); // Reset robot selection when hospital changes
          } catch (error) {
            console.error('Failed to load robots for bulb section:', error);
            setBulbAvailableRobots([]);
          }
        };
        loadBulbRobots();
      }, [selectedBulbHospital, userHospitalId, isHospitalLevel, timeRange, customDateRange]);

      useEffect(() => {
          loadAnalytics();
        }, [timeRange, selectedHospital, selectedRobot, customDateRange]);

        useEffect(() => {
          // Reset loading ref when switching tabs
          loadingTabDataRef.current = false;
          
          // Don't show loading immediately - let the data loading functions control it
          if (activeTab === 'robots') {
            loadRobotPerformance();
            if (isGroupLevel) {
              loadInactiveRobots();
            }
          } else if (activeTab === 'uvc') {
            loadUvcAnalytics();
            loadUvcBulbUsage();
            loadUvcBulbDetail();
            if (isSuperAdmin) loadUvcResetHistory();
          } else if (activeTab === 'operators') {
            loadOperatorDetails();
          } else if (activeTab === 'wards') {
            loadWardBed();
          } else if (activeTab === 'killswitch') {
            loadKillswitchKPIs();
          } else if (activeTab === 'insights') {
            loadAdditionalInsights();
          } else if (activeTab === 'overview' || activeTab === 'compliance' || activeTab === 'trends') {
            // These tabs use data from loadAnalytics which is already loaded/loading
            // Don't set loading to false - loadAnalytics will handle it
            if (!loadingAnalyticsRef.current && (!performanceKPIs.kpis || Object.keys(performanceKPIs.kpis).length === 0)) {
              // If no data yet and not currently loading, trigger a reload
              loadAnalytics();
            }
          }
        }, [activeTab, timeRange, selectedHospital, selectedRobot, customDateRange]);

        // Reload bulb detail when bulb-specific filters change
        useEffect(() => {
          if (activeTab === 'uvc' && (selectedBulbHospital || isHospitalLevel)) {
            loadUvcBulbDetail();
          }
        }, [activeTab, selectedBulbHospital, selectedBulbRobot, timeRange, customDateRange, isHospitalLevel, userHospitalId]);

        const loadRobotPerformance = async () => {
          // Prevent duplicate calls
          if (loadingTabDataRef.current) return;
          loadingTabDataRef.current = true;
          
          setLoading(true);
          const hospitalId = isHospitalLevel ? userHospitalId : selectedHospital;
          const startDate = customDateRange.start || null;
          const endDate = customDateRange.end || null;
          
          try {
            console.log('Loading robot performance...', { hospitalId, timeRange, selectedRobot, startDate, endDate });
            const res = await analyticsAPI.getRobotPerformance(hospitalId, timeRange, selectedRobot, startDate, endDate);
            console.log('Robot performance loaded:', res.data);
            
            // Backend returns the data directly at root level, not nested
            // API response: {robots: [...], protocol_compliance: {...}, side_distribution: [...], version_comparison: [...]}
            const mappedData = {
              robots: res.data?.robots || [],
              protocol_compliance: res.data?.protocol_compliance || {},
              side_distribution: res.data?.side_distribution || [],
              version_comparison: res.data?.version_comparison || []
            };
            console.log('Mapped robot performance:', mappedData);
            setRobotPerformance(mappedData);
          } catch (error) {
            console.error('Failed to load robot performance:', error);
            setRobotPerformance({ robots: [], protocol_compliance: {}, side_distribution: [], version_comparison: [] });
          } finally {
            setLoading(false);
            loadingTabDataRef.current = false;
          }
        };

        const loadInactiveRobots = async () => {
          try {
            const res = await analyticsAPI.getInactiveRobots();
            setInactiveRobots(res.data || { inactive_24h: [], inactive_48h: [], inactive_72h: [], inactive_72plus: [], summary: {} });
          } catch (error) {
            console.error('Failed to load inactive robots:', error);
            setInactiveRobots({ inactive_24h: [], inactive_48h: [], inactive_72h: [], inactive_72plus: [], summary: {} });
          }
        };

        const loadUvcAnalytics = async () => {
          setLoading(true);
          const hospitalId = isHospitalLevel ? userHospitalId : selectedHospital;
          const startDate = customDateRange.start || null;
          const endDate = customDateRange.end || null;
          try {
            const res = await analyticsAPI.getUvcAnalytics(hospitalId, timeRange, startDate, endDate);
            setUvcAnalytics(res.data || { overview: {}, by_hospital: [], daily_trend: [], side_distribution: [], duration_distribution: [] });
          } catch (error) {
            console.error('Failed to load UVC analytics:', error);
            // Set empty data on error (e.g., 404 if endpoint not deployed)
            setUvcAnalytics({ overview: {}, by_hospital: [], daily_trend: [], side_distribution: [], duration_distribution: [] });
          } finally {
            setLoading(false);
          }
        };

        const loadUvcBulbUsage = async () => {
          setLoading(true);
          const hospitalId = isHospitalLevel ? userHospitalId : selectedHospital;
          const startDate = customDateRange.start || null;
          const endDate = customDateRange.end || null;
          try {
            const res = await analyticsAPI.getUvcBulbUsage(hospitalId, timeRange, startDate, endDate);
            setUvcBulbUsage(res.data || { bulb_usage: [], count: 0 });
          } catch (error) {
            console.error('Failed to load UVC bulb usage:', error);
            // Set empty data on error (e.g., 404 if endpoint not deployed)
            setUvcBulbUsage({ bulb_usage: [], count: 0 });
          } finally {
            setLoading(false);
          }
        };

        const loadUvcBulbDetail = async () => {
          const hospitalId = isHospitalLevel ? userHospitalId : (selectedBulbHospital || selectedHospital);
          const startDate = customDateRange.start || null;
          const endDate = customDateRange.end || null;
          
          console.log('Loading bulb detail with params:', {
            hospitalId,
            selectedBulbRobot,
            timeRange,
            startDate,
            endDate,
            isHospitalLevel,
            userHospitalId,
            selectedBulbHospital
          });
          
          try {
            const res = await analyticsAPI.getUvcBulbDetail(hospitalId, selectedBulbRobot, timeRange, startDate, endDate);
            console.log('Bulb detail response:', res.data);

            // Worker returns a flat {bulbs: [{serial, hospital_name, total_hours, ...}]} list;
            // this panel renders hospitals[].robots[].bulbs[], so adapt the shape here.
            const flatBulbs = res.data?.bulbs || [];

            // Reset records give per-robot replacement count and last replacement date.
            let resetsBySerial = {};
            try {
              const resetsRes = await analyticsAPI.getUvcResets();
              (resetsRes.data?.resets || []).forEach(rec => {
                const entry = resetsBySerial[rec.serial] || { count: 0, last: null };
                entry.count += 1;
                if (!entry.last || rec.reset_date > entry.last) entry.last = rec.reset_date;
                resetsBySerial[rec.serial] = entry;
              });
            } catch (e) {
              console.error('Failed to load UVC resets for bulb detail:', e);
            }

            const byHospital = {};
            flatBulbs.forEach(b => {
              const name = b.hospital_name || 'Unknown Hospital';
              if (!byHospital[name]) byHospital[name] = [];
              const resets = resetsBySerial[b.serial] || { count: 0, last: null };
              // All 6 UVC bulbs on a robot run together, so each carries the robot's hours.
              byHospital[name].push({
                serial: b.serial,
                total_hours: b.total_hours || 0,
                bulbs: Array.from({ length: 6 }, (_, i) => ({
                  bulb_num: i + 1,
                  hours_since_replacement: b.total_hours || 0,
                  replacement_count: resets.count,
                  last_replacement: resets.last,
                })),
              });
            });
            const hospitalGroups = Object.entries(byHospital).map(([hospital_name, robots]) => ({ hospital_name, robots }));
            setUvcBulbDetail({ hospitals: hospitalGroups, count: flatBulbs.length });
          } catch (error) {
            console.error('Failed to load UVC bulb detail:', error);
            setUvcBulbDetail({ hospitals: [], count: 0 });
          }
        };

        const loadUvcResetHistory = async () => {
          try {
            const res = await analyticsAPI.getUvcResets();
            setUvcResetHistory(res.data?.resets || []);
          } catch (error) {
            console.error('Failed to load UVC reset history:', error);
            setUvcResetHistory([]);
          }
        };

        const handleUvcReset = async () => {
          if (!uvcResetForm.serial || !uvcResetForm.reset_date) {
            alert('Please select a robot and a reset date');
            return;
          }
          setUvcResetSaving(true);
          // Time defaults to midnight so a date-only reset behaves as before.
          const resetDateTime = `${uvcResetForm.reset_date} ${uvcResetForm.reset_time || '00:00'}`;
          try {
            await analyticsAPI.createUvcReset(uvcResetForm.serial, resetDateTime, uvcResetForm.notes || null);
            setShowUvcResetModal(false);
            setUvcResetForm({ serial: '', reset_date: '', reset_time: '', notes: '' });
            alert(`UVC hours reset recorded for ${uvcResetForm.serial}. Bulb hours for this robot now count from ${resetDateTime}.`);
            loadUvcBulbUsage();
            loadUvcBulbDetail();
            loadUvcResetHistory();
          } catch (error) {
            console.error('Failed to record UVC reset:', error);
            alert(error.response?.data?.detail || 'Failed to record UVC reset. Please try again.');
          } finally {
            setUvcResetSaving(false);
          }
        };

        const loadUvcSessionsDetail = async (durationRange) => {
          setLoading(true);
          const hospitalId = isHospitalLevel ? userHospitalId : selectedHospital;
          const startDate = customDateRange.start || null;
          const endDate = customDateRange.end || null;
          try {
            const res = await analyticsAPI.getUvcSessionsDetail(hospitalId, timeRange, startDate, endDate, durationRange);
            setUvcSessionsDetail(res.data || { sessions: [], count: 0, duration_range: durationRange });
            setShowUvcDrillDown(true);
          } catch (error) {
            console.error('Failed to load UVC session details:', error);
            alert('Failed to load session details');
          } finally {
            setLoading(false);
          }
        };

        const loadOperatorDetails = async () => {
          // Prevent duplicate calls  
          if (loadingTabDataRef.current) return;
          loadingTabDataRef.current = true;
          
          setLoading(true);
          const hospitalId = isHospitalLevel ? userHospitalId : selectedHospital;
          const startDate = customDateRange.start || null;
          const endDate = customDateRange.end || null;
          
          try {
            console.log('Loading operator details...', { hospitalId, timeRange, selectedRobot, startDate, endDate });
            const res = await analyticsAPI.getOperatorDetails(hospitalId, timeRange, 50, selectedRobot, startDate, endDate);
            console.log('Operator details loaded:', res.data);
            
            // Backend returns the data directly at root level, not nested
            // API response: {operators: [...], error_details: [...], shift_performance: [...], role_performance: [...]}
            const mappedData = {
              operators: res.data?.operators || [],
              error_details: res.data?.error_details || [],
              shift_performance: res.data?.shift_performance || [],
              role_performance: res.data?.role_performance || []
            };
            console.log('Mapped operator details:', mappedData);
            setOperatorDetails(mappedData);
          } catch (error) {
            console.error('Failed to load operator details:', error);
            setOperatorDetails({ operators: [], error_details: [], shift_performance: [], role_performance: [] });
          } finally {
            setLoading(false);
            loadingTabDataRef.current = false;
          }
        };

        const loadWardBed = async () => {
          setLoading(true);
          const hospitalId = isHospitalLevel ? userHospitalId : selectedHospital;
          const startDate = customDateRange.start || null;
          const endDate = customDateRange.end || null;
          try {
            const res = await analyticsAPI.getWardBed(hospitalId, timeRange, selectedRobot, startDate, endDate);
            setWardBed(res.data || { wards: [], beds: [], ward_types: [], coverage: {}, sluice_vs_beds: [], sluice_by_ward: [] });
          } catch (error) {
            console.error('Failed to load ward/bed data:', error);
          } finally {
            setLoading(false);
          }
        };

        const loadFleetSessions = async (robot) => {
          const hospitalId = isHospitalLevel ? userHospitalId : selectedHospital;
          const startDate = customDateRange.start || null;
          const endDate = customDateRange.end || null;
          try {
            const res = await analyticsAPI.getFleetSessions(
              hospitalId, 
              robot.serial, // Use serial as robot_id
              null, // operatorId
              null, // wardId
              null, // bedId
              timeRange, 
              startDate, 
              endDate
            );
            setFleetSessions(res.data.sessions || []);
            setSelectedFleetRobot(robot);
            setShowFleetModal(true);
          } catch (error) {
            console.error('Failed to load fleet sessions:', error);
            alert('Failed to load detailed sessions. Please try again.');
          }
        };

        const loadEmergencyStopSessions = async (hospital) => {
          const startDate = customDateRange.start || null;
          const endDate = customDateRange.end || null;
          console.log('Loading emergency stops for hospital:', hospital);
          console.log('Hospital ID:', hospital.hospital_id);
          
          try {
            // Use getSessions with error_code filter for emergency stops
            const res = await analyticsAPI.getSessions(
              hospital.hospital_id,
              null, // robotId
              startDate,
              endDate,
              1000, // limit - get more records
              1, // page
              'Emergency Stop Activated' // error_code
            );
            
            console.log('Emergency stop sessions response:', res.data);
            
            // Transform D1 field names to match backend format expected by modal
            const transformedSessions = (res.data.sessions || []).map(session => ({
              MachineStartTime: session.start_time,
              CleaningCycleEndTime: session.end_time,
              Serial: session.robot_serial,
              Operator: session.operator_name,
              WardName: session.ward_name,
              BedName: session.bed_name,
              TimeMinutes: session.duration_minutes ? `${session.duration_minutes} min` : '0 min',
              Code: session.error_code || 'Emergency Stop Activated',
              Hospital_Name: session.hospital_name
            }));
            
            setEmergencyStopSessions(transformedSessions);
            setSelectedEmergencyHospital(hospital);
            setShowEmergencyModal(true);
          } catch (error) {
            console.error('Failed to load emergency stop sessions:', error);
            alert('Failed to load emergency stop sessions. Please try again.');
          }
        };

        const loadAdditionalInsights = async () => {
          setLoading(true);
          const hospitalId = isHospitalLevel ? userHospitalId : selectedHospital;
          const startDate = customDateRange.start || null;
          const endDate = customDateRange.end || null;
          try {
            const res = await analyticsAPI.getInsights(hospitalId, timeRange, selectedRobot, startDate, endDate);
            console.log('Additional insights loaded:', res.data);
            
            // Backend returns data directly at root level
            const defaultData = { 
              duration_analysis: [], 
              day_of_week: [], 
              peak_hours: [], 
              sync_health: {}, 
              period_stats: {}, 
              database_totals: {}, 
              incomplete_cycles: { 
                total_incomplete: 0, 
                affected_robots: 0, 
                affected_operators: 0, 
                affected_hospitals: 0, 
                details: [] 
              } 
            };
            
            setAdditionalInsights(res.data || defaultData);
          } catch (error) {
            console.error('Failed to load insights:', error);
            setAdditionalInsights({ 
              duration_analysis: [], 
              day_of_week: [], 
              peak_hours: [], 
              sync_health: {}, 
              period_stats: {}, 
              database_totals: {}, 
              incomplete_cycles: { 
                total_incomplete: 0, 
                affected_robots: 0, 
                affected_operators: 0, 
                affected_hospitals: 0, 
                details: [] 
              } 
            });
          } finally {
            setLoading(false);
          }
        };

    const loadAnalytics = async () => {
      // Prevent duplicate concurrent calls
      if (loadingAnalyticsRef.current) return;
      loadingAnalyticsRef.current = true;
      setLoading(true);

      try {
        const hospitalId = isHospitalLevel ? userHospitalId : selectedHospital;
        const startDate = customDateRange.start || null;
        const endDate = customDateRange.end || null;

        const defaultKpis = {
          kpis: {},
          top_performers: { robots: [], operators: [] },
          trends: { weekly: [], monthly: [], error_rate: [] },
          week_over_week: {
            this_week_sessions: 0,
            last_week_sessions: 0,
            sessions_change: 0,
            sessions_change_percent: 0,
            this_week_success_rate: 0,
            last_week_success_rate: 0,
          },
          efficiency: { shift_performance: [] },
          compliance: {},
          compliance_gaps: {},
          risk_indicators: { high_error_operators: [], high_error_robots: [] },
        };

        const results = await Promise.all([
          analyticsAPI.getPerformanceKPIs(hospitalId, timeRange, selectedRobot, startDate, endDate)
            .catch(e => { console.error('Failed to load performance KPIs:', e); return { data: defaultKpis }; }),
        ]);

        const kpisData = results[0]?.data || defaultKpis;

        // Convert weekly trends to daily-like format for charts
        let trendsData = [];
        if (kpisData.trends?.weekly?.length > 0) {
          trendsData = kpisData.trends.weekly.map(w => ({
            date: w.week,
            sessions: w.sessions,
            successful: Math.round(w.sessions * (w.success_rate || 0) / 100),
            failed: Math.round(w.sessions * (1 - (w.success_rate || 0) / 100)),
            success_rate: w.success_rate,
            total_hours: 0,
          }));
        }

        setTrends(trendsData);
        setPerformanceKPIs(kpisData);

        if (kpisData.top_performers?.robots) {
          setRobots({ robots: kpisData.top_performers.robots, statistics: {} });
        }
        if (kpisData.top_performers?.operators) {
          setOperators(kpisData.top_performers.operators.slice(0, 15));
        }
        if (kpisData.hourly_distribution) {
          setHourly(kpisData.hourly_distribution);
        }
      } catch (err) {
        console.error('loadAnalytics unexpected error:', err);
      } finally {
        setLoading(false);
        loadingAnalyticsRef.current = false;
      }
    };

    const loadKillswitchKPIs = async () => {
      try {
        const hospitalId = isHospitalLevel ? userHospitalId : selectedHospital;
        const startDate = customDateRange.start || null;
        const endDate = customDateRange.end || null;

        // Calculate days based on timeRange or customDateRange
        let days = timeRange;
        if (customDateRange.start && customDateRange.end) {
          const start = new Date(customDateRange.start);
          const end = new Date(customDateRange.end);
          days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end dates
        }

        const res = await analyticsAPI.getKillswitchKPIs(days, hospitalId, selectedRobot, startDate, endDate);
        if (res.data) {
          // Use the accurate by_hospital data directly from the API
          // No need to rebuild it from latest_events
          setKillswitchKPIs(res.data);
        }
      } catch (error) {
        console.error('Failed to load killswitch KPIs:', error);
        // Keep existing state if fetch fails
      }
    };

  const exportToCSV = (data, filename, columns) => {
    if (!data || !Array.isArray(data) || data.length === 0) return;
    // When columns are provided (from a table), export only the visible columns with their display labels
    const keys = columns ? columns.map(c => c.key) : Object.keys(data[0]);
    const headers = columns ? columns.map(c => c.label) : keys;
    const csvContent = [
      headers.map(h => JSON.stringify(h ?? '')).join(','),
      ...data.map(row => keys.map(k => JSON.stringify(row[k] ?? '')).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const exportChartToPNG = (chartId) => {
    const svg = document.querySelector(`#${chartId} svg`);
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width * 2;
      canvas.height = img.height * 2;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      const link = document.createElement('a');
      link.download = `chart_${chartId}_${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  // Constants for Export Modal (moved outside to prevent recreation on every render)
  const EXPORT_ALL_FIELDS = [
    { key: 'session_id', label: 'Session ID' },
    { key: 'robot_serial', label: 'Robot Serial' },
    { key: 'hospital_name', label: 'Hospital' },
    { key: 'ward_name', label: 'Ward' },
    { key: 'bed_name', label: 'Bed' },
    { key: 'operator_name', label: 'Operator' },
    { key: 'operator_role', label: 'Operator Role' },
    { key: 'start_time', label: 'Start Time' },
    { key: 'end_time', label: 'End Time' },
    { key: 'duration_minutes', label: 'Duration (min)' },
    { key: 'diagnostic', label: 'Status' },
    { key: 'error_code', label: 'Error Code' },
    { key: 'uvc_side', label: 'UVC Side' },
    { key: 'robot_version', label: 'Robot Version' },
  ];

  const EXPORT_STATUS_OPTIONS = [
    { value: '', label: 'All Statuses' },
    { value: 'ok', label: 'Successful' },
    { value: 'failed', label: 'Failed' },
  ];

  // Export Modal Component with slice and dice options
  const ExportModal = ({ onClose }) => {
    const [exportFilters, setExportFilters] = useState({
      hospital: selectedHospital || '',
      robot: selectedRobot || '',
      operator: '',
      ward: '',
      status: '',
      startDate: customDateRange.start || (isTodaySelected ? new Date().toISOString().split('T')[0] : ''),
      endDate: customDateRange.end || (isTodaySelected ? new Date().toISOString().split('T')[0] : ''),
    });
    const [selectedFields, setSelectedFields] = useState({
      session_id: true,
      robot_serial: true,
      hospital_name: true,
      ward_name: true,
      bed_name: true,
      operator_name: true,
      operator_role: true,
      start_time: true,
      end_time: true,
      duration_minutes: true,
      diagnostic: true,
      error_code: true,
      uvc_side: true,
      robot_version: true,
    });
    const [exporting, setExporting] = useState(false);

    const handleExport = async () => {
      setExporting(true);
      try {
        const D1_WORKER_URL = import.meta.env.VITE_D1_WORKER_URL || 'https://evesix-analytics-worker.reshigan-085.workers.dev';
        const params = new URLSearchParams();
        
        if (exportFilters.hospital) params.append('hospital_id', exportFilters.hospital);
        if (exportFilters.robot) params.append('robot_id', exportFilters.robot);
        if (exportFilters.operator) params.append('operator_id', exportFilters.operator);
        if (exportFilters.ward) params.append('ward_id', exportFilters.ward);
        if (exportFilters.status) params.append('diagnostic', exportFilters.status);
        if (exportFilters.startDate) params.append('start_date', exportFilters.startDate);
        if (exportFilters.endDate) params.append('end_date', exportFilters.endDate);
        
        // Add selected fields
        const fields = Object.entries(selectedFields)
          .filter(([_, selected]) => selected)
          .map(([key]) => key)
          .join(',');
        params.append('fields', fields);
        
        const url = `${D1_WORKER_URL}/analytics/sessions/export?${params.toString()}`;
        window.open(url, '_blank');
        onClose();
      } catch (error) {
        console.error('Error exporting session data:', error);
        alert('Failed to export session data. Please try again.');
      } finally {
        setExporting(false);
      }
    };

    const toggleAllFields = (selected) => {
      const newFields = {};
      EXPORT_ALL_FIELDS.forEach(f => newFields[f.key] = selected);
      setSelectedFields(newFields);
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
          <div className="p-6 border-b border-slate-200 flex justify-between items-center bg-slate-50">
            <div>
              <h3 className="text-xl font-semibold text-slate-800">Export Session Data</h3>
              <p className="text-sm text-slate-500 mt-1">Select filters and fields to customize your export</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-lg transition-colors">
              <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="p-6 overflow-auto max-h-[60vh]">
            {/* Filters Section */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filter Data
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Hospital</label>
                  <select
                    value={exportFilters.hospital}
                    onChange={(e) => setExportFilters(prev => ({ ...prev, hospital: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">All Hospitals</option>
                    {hospitals.map(h => (
                      <option key={h.id} value={h.id}>{h.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Robot</label>
                  <select
                    value={exportFilters.robot}
                    onChange={(e) => setExportFilters(prev => ({ ...prev, robot: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">All Robots</option>
                    {availableRobots.map((r, idx) => (
                      <option key={`export-robot-${idx}-${r.serial}`} value={r.serial}>{r.serial}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Status</label>
                  <select
                    value={exportFilters.status}
                    onChange={(e) => setExportFilters(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {EXPORT_STATUS_OPTIONS.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={exportFilters.startDate}
                    onChange={(e) => setExportFilters(prev => ({ ...prev, startDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">End Date</label>
                  <input
                    type="date"
                    value={exportFilters.endDate}
                    onChange={(e) => setExportFilters(prev => ({ ...prev, endDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Fields Selection Section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  Select Fields to Export
                </h4>
                <div className="flex gap-2">
                  <button
                    onClick={() => toggleAllFields(true)}
                    className="text-xs px-2 py-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                  >
                    Select All
                  </button>
                  <button
                    onClick={() => toggleAllFields(false)}
                    className="text-xs px-2 py-1 text-slate-600 hover:bg-slate-100 rounded transition-colors"
                  >
                    Clear All
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {EXPORT_ALL_FIELDS.map(field => (
                  <label
                    key={field.key}
                    className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                      selectedFields[field.key]
                        ? 'bg-blue-50 border-blue-200 text-blue-700'
                        : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedFields[field.key]}
                      onChange={(e) => setSelectedFields(prev => ({ ...prev, [field.key]: e.target.checked }))}
                      className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                    />
                    <span className="text-sm">{field.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="p-6 border-t border-slate-200 bg-slate-50 flex justify-between items-center">
            <p className="text-xs text-slate-500">
              Export will include up to 50,000 records matching your filters
            </p>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-slate-600 hover:bg-slate-200 rounded-lg text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleExport}
                disabled={exporting || Object.values(selectedFields).every(v => !v)}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
              >
                {exporting ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Exporting...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export CSV
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

    // Use KPIs data from D1 Worker if available, otherwise calculate from trends
    const kpis = performanceKPIs?.kpis || {};
    const totalSessions = kpis.total_sessions || trends.reduce((sum, t) => sum + (t.sessions || 0), 0);
    const totalSuccessful = kpis.total_sessions && kpis.success_rate 
      ? Math.round(kpis.total_sessions * kpis.success_rate / 100)
      : trends.reduce((sum, t) => sum + (t.successful || 0), 0);
    const totalFailed = kpis.failed_sessions || trends.reduce((sum, t) => sum + (t.failed || 0), 0);
    const overallSuccessRate = kpis.success_rate || (totalSessions > 0 ? ((totalSuccessful / totalSessions) * 100).toFixed(1) : 0);
    const totalHours = kpis.total_uvc_hours || trends.reduce((sum, t) => sum + (t.total_hours || 0), 0).toFixed(1);

    const DrillDownModal = ({ data, title, onClose }) => (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-5xl w-full max-h-[85vh] overflow-hidden shadow-2xl">
          <div className="p-6 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
            <div className="flex gap-3">
              <button
                onClick={() => exportToCSV(data, title.toLowerCase().replace(/\s+/g, '_'))}
                className="px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Export CSV
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
          <div className="p-6 overflow-auto max-h-[65vh]">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  {data && data.length > 0 && Object.keys(data[0]).map(key => (
                    <th key={key} className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {key.replace(/_/g, ' ')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data && data.map((row, i) => (
                  <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                    {Object.values(row).map((val, j) => (
                      <td key={j} className="py-3 px-4 text-sm text-gray-700">
                        {typeof val === 'number' ? val.toLocaleString() : String(val)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );

        // Wellsync-style ChartCard component - mature corporate healthcare aesthetic
      const ChartCard = ({ id, title, children, onDrillDown, data, exportable = true, subtitle }) => (
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300">
          <div className="px-6 py-5 border-b border-slate-100 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-blue-50 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-800">{title}</h3>
                {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
              </div>
            </div>
            <div className="flex gap-2">
              {exportable && (
                <>
                  <button
                    onClick={() => exportToCSV(data, title.toLowerCase().replace(/\s+/g, '_'))}
                    className="p-2.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-colors"
                    title="Export CSV"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => exportChartToPNG(id)}
                    className="p-2.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-colors"
                    title="Export PNG"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </button>
                </>
              )}
              {onDrillDown && (
                <button
                  onClick={onDrillDown}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-semibold transition-colors shadow-sm shadow-blue-500/30"
                >
                  Drill Down
                </button>
              )}
            </div>
          </div>
          <div id={id} className="p-6 bg-white">
            {children}
          </div>
        </div>
      );

    if (loading && trends.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center bg-white p-10 rounded-3xl shadow-xl border border-slate-100">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
          <p className="text-slate-800 font-semibold text-xl">Loading Analytics</p>
          <p className="text-slate-500 text-sm mt-2">Preparing your insights dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-2xl p-8 flex flex-col items-center gap-4">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-slate-200 rounded-full"></div>
              <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
            </div>
            <div className="text-slate-700 font-semibold text-lg">Loading analytics...</div>
            <div className="text-slate-500 text-sm">Please wait while we fetch your data</div>
          </div>
        </div>
      )}
            {drillDown && (
              <DrillDownModal
                data={drillDown.data}
                title={drillDown.title}
                onClose={() => setDrillDown(null)}
              />
            )}

            {showReportScheduler && (
              <ReportScheduler
                user={user}
                onClose={() => setShowReportScheduler(false)}
              />
            )}

            {/* UVC Session Drill-Down Modal */}
            {showUvcDrillDown && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
                  <div className="p-6 border-b border-slate-200 flex justify-between items-center bg-gradient-to-r from-blue-50 to-indigo-50">
                    <div>
                      <h3 className="text-xl font-semibold text-slate-800">UV-C Session Details</h3>
                      <p className="text-sm text-slate-600 mt-1">
                        Duration Range: <span className="font-semibold">{uvcSessionsDetail.duration_range}</span> • {uvcSessionsDetail.count} sessions
                      </p>
                    </div>
                    <button 
                      onClick={() => setShowUvcDrillDown(false)} 
                      className="p-2 hover:bg-white/50 rounded-lg transition-colors"
                    >
                      <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  
                  <div className="p-6 overflow-auto max-h-[70vh]">
                    {uvcSessionsDetail.sessions.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-200">
                          <thead className="bg-gradient-to-r from-slate-50 to-slate-100 sticky top-0">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Hospital</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Robot</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Start Time</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">End Time</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Duration</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Status</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Bed</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Ward</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Operator</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-slate-100">
                            {uvcSessionsDetail.sessions.map((session, idx) => (
                              <tr key={`uvc-session-${idx}-${session.Robot_Serial}-${session.CleaningCycleStartTime}`} className="hover:bg-blue-50/50 transition-colors">
                                <td className="px-4 py-3 text-sm font-medium text-slate-900">{session.Hospital_Name}</td>
                                <td className="px-4 py-3 text-sm text-slate-700 font-mono">{session.Robot_Serial}</td>
                                <td className="px-4 py-3 text-sm text-slate-600">{session.CleaningCycleStartTime}</td>
                                <td className="px-4 py-3 text-sm text-slate-600">{session.CleaningCycleEndTime}</td>
                                <td className="px-4 py-3 text-sm">
                                  <span className={`px-2 py-1 rounded-md font-medium ${
                                    session.Time_Minutes >= 10 ? 'bg-green-100 text-green-800' : 
                                    session.Time_Minutes >= 5 ? 'bg-yellow-100 text-yellow-800' : 
                                    'bg-red-100 text-red-800'
                                  }`}>
                                    {session.Time_Minutes > 10 ? '10' : session.Time_Minutes} min
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm">
                                  <span className={`px-2 py-1 rounded-md text-xs font-medium ${
                                    session.Code === 'OK' || session.Code === 'Completed' ? 'bg-green-100 text-green-800' : 
                                    session.Code === 'Error' || session.Code === 'Failed' ? 'bg-red-100 text-red-800' : 
                                    'bg-yellow-100 text-yellow-800'
                                  }`}>
                                    {session.Code || 'N/A'}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-slate-700">{session.Bed_Name}</td>
                                <td className="px-4 py-3 text-sm text-slate-700">{session.Ward_Name}</td>
                                <td className="px-4 py-3 text-sm text-slate-700">{session.Operator}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-16 text-slate-500">
                        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-slate-100 mb-4">
                          <svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <p className="text-lg font-medium text-slate-700">No sessions found</p>
                        <p className="text-sm mt-2 text-slate-500">Try adjusting your filters or date range</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

      {/* Wellsync-style Header - Clean corporate aesthetic */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-xl font-bold tracking-tight">
                    {isGroupLevel ? 'Group Analytics' : isRegionalLevel ? 'Regional  Analytics' : 'Hospital Analytics'}
                  </h1>
                  <p className="text-slate-400 text-sm">
                    {isGroupLevel ? 'Enterprise-wide performance insights' : 
                     isRegionalLevel ? 'Multi-hospital performance overview' : 
                     'Infection control performance metrics'}
                  </p>
                </div>
              </div>
            </div>
                        <div className="flex flex-wrap gap-3">
                          {(isGroupLevel || isRegionalLevel) && (
                            <select
                              value={selectedHospital || ''}
                              onChange={(e) => setSelectedHospital(e.target.value ? parseInt(e.target.value) : null)}
                              className="bg-slate-50 text-slate-700 rounded-xl px-4 py-2.5 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[180px] text-sm font-medium"
                            >
                              <option value="">All Hospitals</option>
                              {hospitals.map(h => (
                                <option key={h.id} value={h.id}>{h.name}</option>
                              ))}
                            </select>
                          )}
                          {/* Robot Filter */}
                          <select
                            value={selectedRobot || ''}
                            onChange={(e) => setSelectedRobot(e.target.value || null)}
                            className="bg-slate-50 text-slate-700 rounded-xl px-4 py-2.5 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[140px] text-sm font-medium"
                          >
                            <option value="">All Robots</option>
                            {availableRobots.map((r, idx) => (
                              <option key={`filter-robot-${idx}-${r.serial}`} value={r.serial}>{r.serial}</option>
                            ))}
                          </select>
                          {/* Time Range Selector */}
                          <select
                            value={showCustomDatePicker ? 'custom' : (isTodaySelected ? 'today' : (timeRange === 36500 ? 'all' : timeRange))}
                            onChange={(e) => {
                              if (e.target.value === 'custom') {
                                setShowCustomDatePicker(true);
                                setIsTodaySelected(false);
                                setTimeRange(null); // Don't send days param when using custom dates
                              } else if (e.target.value === 'today') {
                                // Use days=1 for today (more reliable than date range)
                                setShowCustomDatePicker(false);
                                setIsTodaySelected(true);
                                setTimeRange(1); // Use 1 day lookback
                                setCustomDateRange({ start: '', end: '' });
                                setLoading(true); // Show loading spinner
                              } else if (e.target.value === 'all') {
                                setShowCustomDatePicker(false);
                                setIsTodaySelected(false);
                                setTimeRange(36500); // 100 years = all time
                                setCustomDateRange({ start: '', end: '' });
                                setLoading(true);
                              } else {
                                setShowCustomDatePicker(false);
                                setIsTodaySelected(false);
                                setTimeRange(parseInt(e.target.value));
                                setCustomDateRange({ start: '', end: '' });
                                setLoading(true); // Show loading spinner
                              }
                            }}
                            className="bg-slate-50 text-slate-700 rounded-xl px-4 py-2.5 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-medium"
                          >
                            <option value="today">Today</option>
                            <option value={7}>Last 7 Days</option>
                            <option value={14}>Last 14 Days</option>
                            <option value={30}>Last 30 Days</option>
                            <option value={60}>Last 60 Days</option>
                            <option value={90}>Last 90 Days</option>
                            <option value="all">All Time</option>
                            <option value="custom">Custom Range</option>
                          </select>
                          {/* Custom Date Range Picker */}
                          {showCustomDatePicker && (
                            <div className="flex items-center gap-2">
                              <input
                                type="date"
                                value={customDateRange.start}
                                onChange={(e) => {
                                  setCustomDateRange(prev => ({ ...prev, start: e.target.value }));
                                  if (customDateRange.end && e.target.value) setLoading(true);
                                }}
                                className="bg-slate-50 text-slate-700 rounded-xl px-4 py-2.5 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                              />
                              <span className="text-slate-400">to</span>
                              <input
                                type="date"
                                value={customDateRange.end}
                                onChange={(e) => {
                                  setCustomDateRange(prev => ({ ...prev, end: e.target.value }));
                                  if (customDateRange.start && e.target.value) setLoading(true);
                                }}
                                className="bg-slate-50 text-slate-700 rounded-xl px-4 py-2.5 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                              />
                            </div>
                          )}
                    <button
                      onClick={() => { 
                        loadAnalytics();
                        // Also reload the current tab's data
                        if (activeTab === 'robots') loadRobotPerformance();
                        else if (activeTab === 'uvc') {
                          loadUvcAnalytics();
                          loadUvcBulbUsage();
                          loadUvcBulbDetail();
                        }
                        else if (activeTab === 'operators') loadOperatorDetails();
                        else if (activeTab === 'insights') loadAdditionalInsights();
                      }}
                      className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-colors text-sm flex items-center gap-2 shadow-sm shadow-blue-500/30"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Refresh
                    </button>
                    <button
                      onClick={() => setShowExportModal(true)}
                      className="px-5 py-2.5 bg-slate-700 hover:bg-slate-800 text-white rounded-xl font-semibold transition-colors text-sm flex items-center gap-2 shadow-sm"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Export Data
                    </button>
                    {(isGroupLevel || isRegionalLevel) && (
                      <button
                        onClick={() => setShowReportScheduler(true)}
                        className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold transition-colors text-sm flex items-center gap-2 shadow-sm shadow-emerald-500/30"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Reports
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

      {/* Wellsync-style Pill Tab Navigation */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex gap-2 overflow-x-auto">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-5 py-2.5 font-medium text-sm transition-all rounded-full whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white shadow-md shadow-blue-500/30'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">

            {/* Overview Tab Content */}
            {activeTab === 'overview' && (
              <>
            {/* Wellsync-style KPI Layout - Featured card + Activity cards */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
        {/* Featured Stats Card (like Wellsync's Vital Stats) */}
        <div className="lg:col-span-3">
          <KPICard
            title="Total Sessions"
            value={totalSessions.toLocaleString()}
            subtitle={`${isTodaySelected ? 'Today' : timeRange + ' day period'}`}
            trend={performanceKPIs.kpis?.sessions_change}
            icon="sessions"
            featured={true}
          />
        </div>
        
        {/* Activity Cards Grid (like Wellsync's My Activity) */}
        <div className="lg:col-span-9">
          <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-slate-800">Performance Overview</h3>
              </div>
            </div>
            
            {/* Activity Metrics Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-slate-50 to-white rounded-xl p-4 border border-slate-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-slate-500 font-medium">Success Rate</span>
                </div>
                <p className="text-2xl font-bold text-slate-800">{performanceKPIs.kpis?.success_rate || overallSuccessRate}%</p>
                <p className="text-xs text-slate-400 mt-1">{totalSuccessful.toLocaleString()} successful</p>
              </div>
              
              <div className="bg-gradient-to-br from-slate-50 to-white rounded-xl p-4 border border-slate-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-slate-500 font-medium">UV-C Hours</span>
                  <span className="text-xs text-cyan-600 font-bold flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    total
                  </span>
                </div>
                <p className="text-2xl font-bold text-slate-800">{performanceKPIs.kpis?.total_uvc_hours || totalHours}</p>
                <p className="text-xs text-slate-400 mt-1">Total disinfection time</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Secondary KPI Cards - Wellsync style */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
        <KPICard
          title="Avg Sessions/Day"
          value={performanceKPIs.kpis?.avg_sessions_per_day || 0}
          subtitle="Daily throughput"
          icon="sessions"
          color="primary"
          tooltip="Average number of UV disinfection sessions completed per day. Higher values indicate better facility coverage and operational efficiency. Target: 50+ sessions/day for optimal infection control."
        />
        <KPICard
          title="Avg Sessions/Robot"
          value={performanceKPIs.kpis?.avg_sessions_per_robot || 0}
          subtitle="Robot utilization"
          icon="robot"
          color="primary"
          tooltip="Average sessions completed per robot in the selected period. Measures robot utilization efficiency. Low values may indicate underutilized equipment or maintenance issues."
        />
        <KPICard
          title="Avg Sessions/Operator"
          value={performanceKPIs.kpis?.avg_sessions_per_operator || 0}
          subtitle="Operator productivity"
          icon="operator"
          color="primary"
          tooltip="Average sessions completed per operator. Measures staff productivity and workload distribution. Helps identify training needs and optimize shift scheduling."
        />
        <KPICard
          title="Active Robots"
          value={performanceKPIs.kpis?.active_robots || robots.statistics?.active_robots || 0}
          subtitle="Active in period"
          icon="robot"
          color="success"
          tooltip="Number of robots that completed at least one disinfection session in the selected period. Indicates fleet availability and operational capacity."
        />
        <KPICard
          title="Error Rate"
          value={`${performanceKPIs.kpis?.error_rate || 0}%`}
          subtitle={`${performanceKPIs.kpis?.failed_sessions || totalFailed} failed`}
          icon="error"
          color="danger"
          tooltip="Percentage of sessions that failed or encountered errors. Target: below 5%. High error rates may indicate equipment issues, operator training needs, or environmental factors."
        />
      </div>

      {/* Main Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Sessions Trend */}
        <ChartCard
          id="sessions-trend"
          title="Sessions Trend"
          data={trends}
          onDrillDown={() => setDrillDown({ data: trends, title: 'Daily Sessions Data' })}
        >
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={trends}>
              <defs>
                <linearGradient id="colorSessions" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorSuccess" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.success} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={COLORS.success} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" vertical={false} />
              <XAxis dataKey="date" stroke="#9ca3af" tick={{ fontSize: 11 }} />
              <YAxis stroke="#9ca3af" tick={{ fontSize: 11 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Area type="monotone" dataKey="sessions" name="Total Sessions" stroke={COLORS.primary} fill="url(#colorSessions)" strokeWidth={2} />
              <Area type="monotone" dataKey="successful" name="Successful" stroke={COLORS.success} fill="url(#colorSuccess)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Success Rate Trend */}
        <ChartCard
          id="success-rate-trend"
          title="Success Rate Trend"
          data={trends}
          onDrillDown={() => setDrillDown({ data: trends, title: 'Success Rate Data' })}
        >
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={trends}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" vertical={false} />
              <XAxis dataKey="date" stroke="#9ca3af" tick={{ fontSize: 11 }} />
              <YAxis stroke="#9ca3af" tick={{ fontSize: 11 }} domain={[0, 100]} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line type="monotone" dataKey="success_rate" name="Success Rate %" stroke={COLORS.success} strokeWidth={3} dot={{ fill: COLORS.success, strokeWidth: 2 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Robot & Operator Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Robot Performance */}
        <ChartCard
          id="robot-performance"
          title="Robot Performance (Top 10)"
          data={robots.robots?.slice(0, 10)}
          onDrillDown={() => setDrillDown({ data: robots.robots, title: 'All Robot Performance Data' })}
        >
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={robots.robots?.slice(0, 10)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" vertical={false} />
              <XAxis type="number" stroke="#9ca3af" tick={{ fontSize: 11 }} />
              <YAxis dataKey="serial" type="category" stroke="#9ca3af" tick={{ fontSize: 10 }} width={80} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar dataKey="total_sessions" name="Sessions" fill={COLORS.primary} radius={[0, 4, 4, 0]} />
              <Bar dataKey="failed" name="Failed" fill={COLORS.danger} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Operator Performance */}
        <ChartCard
          id="operator-performance"
          title="Top Operators"
          data={operators}
          onDrillDown={() => setDrillDown({ data: operators, title: 'All Operator Performance Data' })}
        >
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={operators.slice(0, 10)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" vertical={false} />
              <XAxis type="number" stroke="#9ca3af" tick={{ fontSize: 11 }} />
              <YAxis dataKey="name" type="category" stroke="#9ca3af" tick={{ fontSize: 10 }} width={100} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar dataKey="total_sessions" name="Sessions" fill={COLORS.chartColors[4]} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Hourly Distribution & Error Analysis */}
      <div className="grid grid-cols-1 gap-6 mb-6">
        {/* Hourly Distribution */}
        <ChartCard
          id="hourly-distribution"
          title="Hourly Activity Distribution"
          data={hourly}
          onDrillDown={() => setDrillDown({ data: hourly, title: 'Hourly Activity Data' })}
        >
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={hourly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" vertical={false} />
              <XAxis dataKey="label" stroke="#9ca3af" tick={{ fontSize: 10 }} />
              <YAxis stroke="#9ca3af" tick={{ fontSize: 11 }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="sessions" name="Sessions" fill={COLORS.accent} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Hospital Comparison (for Group/Regional  users) */}
      {(isGroupLevel || isRegionalLevel) && hospitals.length > 0 && (
        <ChartCard
          id="hospital-comparison"
          title="Hospital Performance Comparison"
          data={hospitals}
          onDrillDown={() => setDrillDown({ data: hospitals, title: 'Hospital Comparison Data' })}
        >
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={hospitals.slice(0, 15)}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" vertical={false} />
              <XAxis dataKey="name" stroke="#9ca3af" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={80} />
              <YAxis stroke="#9ca3af" tick={{ fontSize: 11 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar dataKey="total_sessions" name="Sessions" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
              <Bar dataKey="successful" name="Successful" fill={COLORS.success} radius={[4, 4, 0, 0]} />
              <Bar dataKey="failed" name="Failed" fill={COLORS.danger} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {/* Weekly & Monthly Performance Trends */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Weekly Trend */}
        {performanceKPIs.trends?.weekly?.length > 0 && (
          <ChartCard
            id="weekly-trend"
            title="Weekly Performance Trend (Last 8 Weeks)"
            data={performanceKPIs.trends.weekly}
            onDrillDown={() => setDrillDown({ data: performanceKPIs.trends.weekly, title: 'Weekly Performance Data' })}
          >
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={performanceKPIs.trends.weekly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" vertical={false} />
                <XAxis dataKey="week" stroke="#9ca3af" tick={{ fontSize: 10 }} />
                <YAxis yAxisId="left" stroke="#9ca3af" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" stroke="#9ca3af" tick={{ fontSize: 11 }} domain={[0, 100]} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line yAxisId="left" type="monotone" dataKey="sessions" name="Sessions" stroke={COLORS.primary} strokeWidth={2} dot={{ fill: COLORS.primary }} />
                <Line yAxisId="right" type="monotone" dataKey="success_rate" name="Success Rate %" stroke={COLORS.success} strokeWidth={2} dot={{ fill: COLORS.success }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        )}

        {/* Monthly Trend */}
        {performanceKPIs.trends?.monthly?.length > 0 && (
          <ChartCard
            id="monthly-trend"
            title="Monthly Performance Trend (Last 6 Months)"
            data={performanceKPIs.trends.monthly}
            onDrillDown={() => setDrillDown({ data: performanceKPIs.trends.monthly, title: 'Monthly Performance Data' })}
          >
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={performanceKPIs.trends.monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" vertical={false} />
                <XAxis dataKey="month" stroke="#9ca3af" tick={{ fontSize: 10 }} />
                <YAxis yAxisId="left" stroke="#9ca3af" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" stroke="#9ca3af" tick={{ fontSize: 11 }} domain={[0, 100]} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar yAxisId="left" dataKey="sessions" name="Sessions" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
                <Line yAxisId="right" type="monotone" dataKey="success_rate" name="Success Rate %" stroke={COLORS.success} strokeWidth={3} dot={{ fill: COLORS.success, strokeWidth: 2 }} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}
      </div>

      {/* Error Rate Trend */}
      {performanceKPIs.trends?.error_rate?.length > 0 && (
        <ChartCard
          id="error-rate-trend"
          title="Error Rate Trend (Last 30 Days)"
          data={performanceKPIs.trends.error_rate}
          onDrillDown={() => setDrillDown({ data: performanceKPIs.trends.error_rate, title: 'Error Rate Trend Data' })}
        >
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={performanceKPIs.trends.error_rate}>
              <defs>
                <linearGradient id="colorErrorRate" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.danger} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={COLORS.danger} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" vertical={false} />
              <XAxis dataKey="date" stroke="#9ca3af" tick={{ fontSize: 10 }} />
              <YAxis stroke="#9ca3af" tick={{ fontSize: 11 }} domain={[0, 'auto']} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="error_rate" name="Error Rate %" stroke={COLORS.danger} fill="url(#colorErrorRate)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {/* Top Performers Section */}
      {(performanceKPIs.top_performers?.robots?.length > 0 || performanceKPIs.top_performers?.operators?.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Top Performing Robots */}
          {performanceKPIs.top_performers?.robots?.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="p-4 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900">Top Performing Robots</h3>
                <p className="text-sm text-gray-500">Ranked by success rate (min 50 sessions)</p>
              </div>
              <div className="p-4">
                {performanceKPIs.top_performers.robots.map((robot, index) => (
                  <div key={`top-robot-${index}-${robot.serial}`} className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0">
                    <div className="flex items-center gap-3">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        index === 0 ? 'bg-yellow-100 text-yellow-700' :
                        index === 1 ? 'bg-gray-100 text-gray-600' :
                        index === 2 ? 'bg-orange-100 text-orange-700' :
                        'bg-gray-50 text-gray-500'
                      }`}>
                        {index + 1}
                      </span>
                      <div>
                        <p className="font-medium text-gray-900">{robot.serial}</p>
                        <p className="text-xs text-gray-500">{robot.sessions} sessions</p>
                        {robot.hospital_name && <p className="text-xs text-blue-500">{robot.hospital_name}</p>}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-green-600">{robot.success_rate}%</p>
                      <p className="text-xs text-gray-500">success rate</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top Performing Operators */}
          {performanceKPIs.top_performers?.operators?.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="p-4 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900">Top Performing Operators</h3>
                <p className="text-sm text-gray-500">Ranked by success rate (min 30 sessions)</p>
              </div>
              <div className="p-4">
                {performanceKPIs.top_performers.operators.map((operator, index) => (
                  <div key={`top-operator-${index}-${operator.id}`} className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0">
                    <div className="flex items-center gap-3">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        index === 0 ? 'bg-yellow-100 text-yellow-700' :
                        index === 1 ? 'bg-gray-100 text-gray-600' :
                        index === 2 ? 'bg-orange-100 text-orange-700' :
                        'bg-gray-50 text-gray-500'
                      }`}>
                        {index + 1}
                      </span>
                      <div>
                        <p className="font-medium text-gray-900">{operator.name}</p>
                        <p className="text-xs text-gray-500">{operator.sessions} sessions</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-green-600">{operator.success_rate}%</p>
                      <p className="text-xs text-gray-500">success rate</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}


      {/* Robot Fleet Analysis Table */}
      <div className="mt-6">
        <SortableTable
          title="Robot Fleet Analysis"
          data={robots.robots}
          defaultSortKey="total_sessions"
          onExport={() => exportToCSV(robots.robots, 'robot_fleet_analysis')}
          onRowClick={loadFleetSessions}
          columns={[
            { key: 'serial', label: 'Robot ID' },
            { key: 'hospital', label: 'Hospital' },
            { key: 'total_sessions', label: 'Sessions' },
            { key: 'success_rate', label: 'Success Rate', render: (val) => (
              <span className={`font-medium ${val >= 80 ? 'text-emerald-500' : val >= 60 ? 'text-yellow-500' : 'text-red-500'}`}>
                {val}%
              </span>
            )},
            { key: 'avg_duration', label: 'Avg Duration', render: (val) => `${val} min` },
            { key: 'total_hours', label: 'Total Hours', render: (val) => `${val}h` },
            { key: 'status', label: 'Status', render: (val, row) => (
              row.is_high_error ? (
                <span className="px-2 py-1 bg-red-100 text-red-600 rounded-full text-xs font-medium">High Errors</span>
              ) : row.is_outlier_high ? (
                <span className="px-2 py-1 bg-emerald-100 text-emerald-600 rounded-full text-xs font-medium">Top Performer</span>
              ) : row.is_outlier_low ? (
                <span className="px-2 py-1 bg-yellow-100 text-yellow-600 rounded-full text-xs font-medium">Low Usage</span>
              ) : (
                <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">Normal</span>
              )
            )}
          ]}
        />
      </div>
        </>
      )}

      {/* Robot Performance Tab */}
      {activeTab === 'robots' && (
        <>
          {/* Protocol Compliance KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <KPICard title="Single Session Beds" value={robotPerformance.protocol_compliance?.single_session?.toLocaleString() || 0} subtitle={`${robotPerformance.protocol_compliance?.single_pct || 0}% of bed-days`} icon="1️⃣" color="cyan" />
            <KPICard title="Double Session Beds" value={robotPerformance.protocol_compliance?.double_session?.toLocaleString() || 0} subtitle={`${robotPerformance.protocol_compliance?.double_pct || 0}% (in-room protocol)`} icon="2️⃣" color="emerald" />
            <KPICard title="Multiple Sessions" value={robotPerformance.protocol_compliance?.multiple_session?.toLocaleString() || 0} subtitle={`${robotPerformance.protocol_compliance?.multiple_pct || 0}% (high usage)`} icon="🔄" color="purple" />
            <KPICard title="Total Bed-Days" value={robotPerformance.protocol_compliance?.total_bed_days?.toLocaleString() || 0} subtitle="Beds with sessions" icon="🛏️" color="blue" />
          </div>

          {/* Version & Side Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <ChartCard id="version-comparison" title="Robot Version Performance" data={robotPerformance.version_comparison}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={robotPerformance.version_comparison}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" vertical={false} />
                  <XAxis dataKey="version" stroke="#9ca3af" tick={{ fontSize: 11 }} />
                  <YAxis stroke="#9ca3af" tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="total_sessions" name="Sessions" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="successful" name="Successful" fill={COLORS.success} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="failed" name="Failed" fill={COLORS.danger} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard id="side-distribution" title="UV-C Position Distribution" data={robotPerformance.side_distribution}>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={robotPerformance.side_distribution} dataKey="count" nameKey="side" cx="50%" cy="50%" outerRadius={100} innerRadius={50} label={({ side, count }) => `${side}: ${count}`}>
                    {robotPerformance.side_distribution?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS.chartColors[index % COLORS.chartColors.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* Inactive Robots Section - Super Admin Only */}
          {isGroupLevel && (
            <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300">
              <div className="px-6 py-5 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-red-100 to-red-50 rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-slate-800">Inactive Robots Alert</h3>
                    <p className="text-xs text-slate-500 mt-0.5">Robots with no activity in various time periods</p>
                  </div>
                </div>
              </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* 24 Hour Period - Green */}
                <div className="bg-gradient-to-br from-green-50 to-white rounded-xl p-5 border-2 border-green-200">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-bold text-green-800">24 Hours</h4>
                    <span className="bg-green-100 text-green-700 text-xs font-bold px-3 py-1 rounded-full">
                      {inactiveRobots.inactive_24h?.length || 0}
                    </span>
                  </div>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {inactiveRobots.inactive_24h && inactiveRobots.inactive_24h.length > 0 ? (
                      inactiveRobots.inactive_24h.map((robot, idx) => (
                        <div key={`inactive-24h-${idx}-${robot.serial}`} className="bg-white rounded-lg p-3 border border-green-200">
                          <div className="flex items-start justify-between mb-1">
                            <span className="text-sm font-bold text-slate-800">{robot.serial}</span>
                            <span className="text-xs text-green-600 font-medium">{robot.hours_inactive}h</span>
                          </div>
                          <p className="text-xs text-slate-600 mb-1">{robot.hospital}</p>
                          <p className="text-xs text-slate-500 mb-1">Modem: {robot.modem_num || 'N/A'}</p>
                          <p className="text-xs text-slate-500">
                            Last: {robot.last_activity ? new Date(robot.last_activity).toLocaleString() : 'N/A'}
                          </p>
                        </div>
                      ))
                    ) : (
                      <p className="text-xs text-slate-500 text-center py-4">No robots inactive for 24h</p>
                    )}
                  </div>
                </div>

                {/* 48 Hour Period - Yellow/Orange */}
                <div className="bg-gradient-to-br from-yellow-50 to-white rounded-xl p-5 border-2 border-yellow-300">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-bold text-yellow-800">48 Hours</h4>
                    <span className="bg-yellow-100 text-yellow-700 text-xs font-bold px-3 py-1 rounded-full">
                      {inactiveRobots.inactive_48h?.length || 0}
                    </span>
                  </div>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {inactiveRobots.inactive_48h && inactiveRobots.inactive_48h.length > 0 ? (
                      inactiveRobots.inactive_48h.map((robot, idx) => (
                        <div key={`inactive-48h-${idx}-${robot.serial}`} className="bg-white rounded-lg p-3 border border-yellow-300">
                          <div className="flex items-start justify-between mb-1">
                            <span className="text-sm font-bold text-slate-800">{robot.serial}</span>
                            <span className="text-xs text-yellow-600 font-medium">{robot.hours_inactive}h</span>
                          </div>
                          <p className="text-xs text-slate-600 mb-1">{robot.hospital}</p>
                          <p className="text-xs text-slate-500 mb-1">Modem: {robot.modem_num || 'N/A'}</p>
                          <p className="text-xs text-slate-500">
                            Last: {robot.last_activity ? new Date(robot.last_activity).toLocaleString() : 'N/A'}
                          </p>
                        </div>
                      ))
                    ) : (
                      <p className="text-xs text-slate-500 text-center py-4">No robots inactive for 48h</p>
                    )}
                  </div>
                </div>

                {/* 72 Hour Period - Orange */}
                <div className="bg-gradient-to-br from-orange-50 to-white rounded-xl p-5 border-2 border-orange-300">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-bold text-orange-800">72 Hours</h4>
                    <span className="bg-orange-100 text-orange-700 text-xs font-bold px-3 py-1 rounded-full">
                      {inactiveRobots.inactive_72h?.length || 0}
                    </span>
                  </div>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {inactiveRobots.inactive_72h && inactiveRobots.inactive_72h.length > 0 ? (
                      inactiveRobots.inactive_72h.map((robot, idx) => (
                        <div key={`inactive-72h-${idx}-${robot.serial}`} className="bg-white rounded-lg p-3 border border-orange-300">
                          <div className="flex items-start justify-between mb-1">
                            <span className="text-sm font-bold text-slate-800">{robot.serial}</span>
                            <span className="text-xs text-orange-600 font-medium">{robot.hours_inactive}h</span>
                          </div>
                          <p className="text-xs text-slate-600 mb-1">{robot.hospital}</p>
                          <p className="text-xs text-slate-500 mb-1">Modem: {robot.modem_num || 'N/A'}</p>
                          <p className="text-xs text-slate-500">
                            Last: {robot.last_activity ? new Date(robot.last_activity).toLocaleString() : 'N/A'}
                          </p>
                        </div>
                      ))
                    ) : (
                      <p className="text-xs text-slate-500 text-center py-4">No robots inactive for 72h</p>
                    )}
                  </div>
                </div>

                {/* 72+ Hour Period - Red */}
                <div className="bg-gradient-to-br from-red-50 to-white rounded-xl p-5 border-2 border-red-300">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-bold text-red-800">72+ Hours</h4>
                    <span className="bg-red-100 text-red-700 text-xs font-bold px-3 py-1 rounded-full">
                      {inactiveRobots.inactive_72plus?.length || 0}
                    </span>
                  </div>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {inactiveRobots.inactive_72plus && inactiveRobots.inactive_72plus.length > 0 ? (
                      inactiveRobots.inactive_72plus.map((robot, idx) => (
                        <div key={`inactive-72plus-${idx}-${robot.serial}`} className="bg-white rounded-lg p-3 border border-red-300">
                          <div className="flex items-start justify-between mb-1">
                            <span className="text-sm font-bold text-slate-800">{robot.serial}</span>
                            <span className="text-xs text-red-600 font-medium">{robot.hours_inactive}h</span>
                          </div>
                          <p className="text-xs text-slate-600 mb-1">{robot.hospital}</p>
                          <p className="text-xs text-slate-500 mb-1">Modem: {robot.modem_num || 'N/A'}</p>
                          <p className="text-xs text-slate-500">
                            Last: {robot.last_activity ? new Date(robot.last_activity).toLocaleString() : 'N/A'}
                          </p>
                        </div>
                      ))
                    ) : (
                      <p className="text-xs text-slate-500 text-center py-4">No robots inactive for 72+ hours</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          )}
        </>
      )}

      {/* UVC Analytics Tab */}
      {activeTab === 'uvc' && (
        <>
          {/* Bulb Usage Visualization - HIDDEN */}
          {false && (
          <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 mb-6">
            <div className="px-6 py-5 border-b border-slate-100">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-amber-100 to-amber-50 rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-slate-800">Individual Bulb Usage</h3>
                    <p className="text-xs text-slate-500 mt-0.5">6-bulb configuration per robot</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  {!isHospitalLevel && (
                    <select
                      value={selectedBulbHospital || ''}
                      onChange={(e) => setSelectedBulbHospital(e.target.value ? parseInt(e.target.value) : null)}
                      className="bg-slate-50 text-slate-700 rounded-xl px-4 py-2 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm font-medium"
                    >
                      <option value="">All Hospitals</option>
                      {hospitals.map(h => (
                        <option key={h.id} value={h.id}>{h.name}</option>
                      ))}
                    </select>
                  )}
                  <select
                    value={selectedBulbRobot || ''}
                    onChange={(e) => setSelectedBulbRobot(e.target.value || null)}
                    className="bg-slate-50 text-slate-700 rounded-xl px-4 py-2 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm font-medium"
                  >
                    <option value="">All Robots</option>
                    {bulbAvailableRobots.map((r, idx) => (
                      <option key={`bulb-robot-${idx}-${r.serial}`} value={r.serial}>{r.serial}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="p-8">
              {uvcBulbDetail.hospitals && uvcBulbDetail.hospitals.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
                  {uvcBulbDetail.hospitals.map((hospital, hIdx) => (
                    hospital.robots.map((robot, rIdx) => (
                      <div key={`uvc-${hospital.hospital_name}-${robot.serial}-${hIdx}-${rIdx}`} className="bg-gradient-to-br from-slate-50 to-white rounded-xl p-6 border border-slate-200">
                        <div className="text-center mb-6">
                          <h4 className="text-lg font-bold text-slate-800">{hospital.hospital_name}</h4>
                          <p className="text-sm text-slate-600 font-mono mt-1">{robot.serial}</p>
                          <p className="text-xs text-slate-500 mt-2">Total: {robot.total_hours?.toFixed(1) || 0} hours</p>
                        </div>
                        
                        {/* Cylinder with 6 bulbs */}
                        <div className="relative w-full h-80 flex items-center justify-center">
                          {/* Center cylinder */}
                          <div className="absolute w-24 h-64 bg-gradient-to-b from-slate-300 via-slate-200 to-slate-300 rounded-lg flex items-center justify-center shadow-lg">
                            <div className="text-center">
                              <svg className="w-12 h-12 text-slate-600 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              <p className="text-xs text-slate-600 font-bold mt-1">Robot</p>
                            </div>
                          </div>
                          
                          {/* 6 bulbs positioned around cylinder */}
                          {robot.bulbs.map((bulb, idx) => {
                            const angle = (idx * 60 - 90) * (Math.PI / 180); // 60 degrees apart, starting from top
                            const radius = 110; // distance from center
                            const x = Math.cos(angle) * radius;
                            const y = Math.sin(angle) * radius;
                            const hours = bulb.hours_since_replacement || 0;
                            const maxHours = 1000; // Max expected hours
                            const percentage = Math.min((hours / maxHours) * 100, 100);
                            
                            // Color based on usage
                            let bulbColor = 'from-green-400 to-green-600';
                            if (percentage > 75) bulbColor = 'from-red-400 to-red-600';
                            else if (percentage > 50) bulbColor = 'from-orange-400 to-orange-600';
                            else if (percentage > 25) bulbColor = 'from-yellow-400 to-yellow-600';
                            
                            return (
                              <div
                                key={bulb.bulb_num}
                                className="absolute transform -translate-x-1/2 -translate-y-1/2"
                                style={{
                                  left: `calc(50% + ${x}px)`,
                                  top: `calc(50% + ${y}px)`,
                                }}
                              >
                                <div className="relative group cursor-pointer">
                                  {/* Bulb */}
                                  <div className={`w-12 h-16 bg-gradient-to-b ${bulbColor} rounded-full shadow-lg hover:scale-110 transition-transform duration-200 flex items-center justify-center border-2 border-white`}>
                                    <span className="text-white font-bold text-xs">{bulb.bulb_num}</span>
                                  </div>
                                  
                                  {/* Tooltip on hover */}
                                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                                    <div className="bg-slate-900 text-white text-xs rounded-lg py-2 px-3 shadow-xl whitespace-nowrap">
                                      <div className="font-bold mb-1">Bulb {bulb.bulb_num}</div>
                                      <div>Usage: {hours.toFixed(1)}h</div>
                                      <div>Replacements: {bulb.replacement_count}</div>
                                      {bulb.last_replacement && (
                                        <div className="text-slate-300 text-[10px] mt-1">
                                          Last: {new Date(bulb.last_replacement).toLocaleDateString()}
                                        </div>
                                      )}
                                      <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
                                        <div className="border-4 border-transparent border-t-slate-900"></div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        
                        {/* Legend */}
                        <div className="mt-6 pt-4 border-t border-slate-200">
                          <div className="flex justify-between items-center text-xs">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 bg-gradient-to-br from-green-400 to-green-600 rounded-full"></div>
                              <span className="text-slate-600">New (0-25%)</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full"></div>
                              <span className="text-slate-600">Good (25-50%)</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full"></div>
                              <span className="text-slate-600">Warn (50-75%)</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 bg-gradient-to-br from-red-400 to-red-600 rounded-full"></div>
                              <span className="text-slate-600">Replace (75%+)</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-amber-100 mb-4">
                    <svg className="w-10 h-10 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <p className="text-lg font-medium text-slate-700">No bulb data available</p>
                  <p className="text-sm mt-2 text-slate-500">Select a hospital or robot to view bulb usage</p>
                </div>
              )}
            </div>
          </div>
          )}

          {/* UVC Overview KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            <KPICard title="Total UV-C Sessions" value={uvcAnalytics.overview?.total_uvc_sessions?.toLocaleString() || 0} subtitle={`${(uvcAnalytics.overview?.avg_uvc_minutes || 0).toFixed(1)} min avg`} icon="sessions" color="primary" />
            <KPICard title="Total UV-C Hours" value={(uvcAnalytics.overview?.total_uvc_hours || 0).toLocaleString(undefined, {maximumFractionDigits: 1})} subtitle="Disinfection time" icon="time" color="success" />
            <KPICard title="Beds Treated" value={uvcAnalytics.overview?.beds_treated?.toLocaleString() || 0} subtitle="With UV-C treatment" icon="coverage" color="cyan" />
            <KPICard title="Success Rate" value={`${((uvcAnalytics.overview?.successful_sessions || 0) / (uvcAnalytics.overview?.total_uvc_sessions || 1) * 100).toFixed(1)}%`} subtitle={`${uvcAnalytics.overview?.successful_sessions?.toLocaleString() || 0} / ${uvcAnalytics.overview?.total_uvc_sessions?.toLocaleString() || 0} sessions`} icon="success" color="emerald" />
            <KPICard title="Error Rate" value={`${((uvcAnalytics.overview?.error_sessions || 0) / (uvcAnalytics.overview?.total_uvc_sessions || 1) * 100).toFixed(1)}%`} subtitle={`${uvcAnalytics.overview?.error_sessions?.toLocaleString() || 0} / ${uvcAnalytics.overview?.total_uvc_sessions?.toLocaleString() || 0} errors`} icon="error" color="danger" />
          </div>

          {/* Session Duration Distribution */}
          <div className="mb-6">
            <ChartCard id="uvc-duration-distribution" title="Session Duration Distribution" data={uvcAnalytics.duration_distribution}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={uvcAnalytics.duration_distribution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" vertical={false} />
                  <XAxis dataKey="duration_range" stroke="#9ca3af" tick={{ fontSize: 11 }} />
                  <YAxis stroke="#9ca3af" tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar 
                    dataKey="count" 
                    name="Sessions" 
                    fill={COLORS.primary} 
                    radius={[4, 4, 0, 0]} 
                    onClick={(data) => loadUvcSessionsDetail(data.duration_range)}
                    cursor="pointer"
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* Bulb Usage Chart */}
          <div className="mb-6">
            {isSuperAdmin && (
              <div className="flex justify-end mb-3">
                <button
                  onClick={() => {
                    const now = new Date();
                    setUvcResetForm({
                      serial: '',
                      reset_date: now.toISOString().split('T')[0],
                      reset_time: `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`,
                      notes: ''
                    });
                    setShowUvcResetModal(true);
                  }}
                  className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-semibold transition-colors text-sm flex items-center gap-2 shadow-sm shadow-amber-500/30"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Reset UVC Hours
                </button>
              </div>
            )}
            <ChartCard id="uvc-bulb-usage" title="UV-C Bulb Usage by Robot" data={uvcBulbUsage.bulb_usage}>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={uvcBulbUsage.bulb_usage}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" vertical={false} />
                  <XAxis 
                    dataKey="Hospital_and_Robot" 
                    stroke="#9ca3af" 
                    tick={{ fontSize: 9, angle: -45, textAnchor: 'end' }} 
                    height={120}
                    interval={0}
                  />
                  <YAxis stroke="#9ca3af" tick={{ fontSize: 11 }} label={{ value: 'Used Hours', angle: -90, position: 'insideLeft' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar
                    dataKey="used_hours"
                    fill={COLORS.warning}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            {isSuperAdmin && (
              <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm mt-6">
                <div className="px-6 py-5 border-b border-slate-100">
                  <h3 className="text-base font-semibold text-slate-800">UVC Reset History</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Bulb hour resets recorded per robot</p>
                </div>
                {uvcResetHistory.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                          <th className="px-6 py-3">Robot</th>
                          <th className="px-6 py-3">Reset Date & Time</th>
                          <th className="px-6 py-3">Reset By</th>
                          <th className="px-6 py-3">Notes</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {uvcResetHistory.map((rec) => (
                          <tr key={rec.id} className="hover:bg-slate-50">
                            <td className="px-6 py-3 font-mono text-slate-700">{rec.serial}</td>
                            <td className="px-6 py-3 text-slate-700">{rec.reset_date}</td>
                            <td className="px-6 py-3 text-slate-700">{rec.reset_by || '—'}</td>
                            <td className="px-6 py-3 text-slate-500">{rec.notes || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="px-6 py-8 text-sm text-slate-500 text-center">No resets recorded yet.</p>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Operator Performance Tab */}
      {activeTab === 'operators' && (
        <>
          {/* Operator Filter */}
          <div className="bg-white rounded-xl border border-gray-100 p-4 mb-6">
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-black">Filter by Operator:</label>
              <select
                value={selectedOperator || ''}
                onChange={(e) => setSelectedOperator(e.target.value || null)}
                className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Operators</option>
                {operatorDetails.operators?.map((op, i) => (
                  <option key={i} value={op.name}>{op.name}</option>
                ))}
              </select>
              {selectedOperator && (
                <button
                  onClick={() => setSelectedOperator(null)}
                  className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear Filter
                </button>
              )}
            </div>
          </div>

          {/* Shift & Role Performance */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <ChartCard id="shift-performance" title="Shift Performance Comparison" data={operatorDetails.shift_performance}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={operatorDetails.shift_performance}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" vertical={false} />
                  <XAxis dataKey="shift" stroke="#9ca3af" tick={{ fontSize: 11 }} />
                  <YAxis stroke="#9ca3af" tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="total_sessions" name="Sessions" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="successful" name="Successful" fill={COLORS.success} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="failed" name="Failed" fill={COLORS.danger} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard id="role-performance" title="Role Performance Comparison" data={operatorDetails.role_performance}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={operatorDetails.role_performance?.slice(0, 10)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" vertical={false} />
                  <XAxis type="number" stroke="#9ca3af" tick={{ fontSize: 11 }} />
                  <YAxis dataKey="role" type="category" stroke="#9ca3af" tick={{ fontSize: 10 }} width={100} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="total_sessions" name="Sessions" fill={COLORS.chartColors[4]} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* Error Details by Operator */}
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden mb-6">
            <div className="p-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Error Details by Operator</h3>
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[400px] overflow-y-auto">
              {operatorDetails.error_details?.slice(0, 12).map((op, i) => (
                <div key={i} className="bg-gray-100 rounded-xl p-4">
                  <h4 className="text-gray-900 font-medium mb-2">{op.name}</h4>
                  <div className="space-y-1">
                    {op.errors?.map((err, j) => (
                      <div key={j} className="flex justify-between text-sm">
                        <span className="text-gray-500 truncate mr-2">{err.code}</span>
                        <span className="text-red-400 font-medium">{err.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Operator Details Table */}
          <SortableTable
            title={`Operator Performance Details${selectedOperator ? ` (Filtered: ${selectedOperator})` : ''}`}
            data={operatorDetails.operators?.filter(op => !selectedOperator || op.name === selectedOperator)}
            defaultSortKey="total_sessions"
            onExport={() => exportToCSV(operatorDetails.operators?.filter(op => !selectedOperator || op.name === selectedOperator), 'operator_performance_details')}
            columns={[
              { key: 'name', label: 'Operator', render: (val) => val || '-' },
              { key: 'role', label: 'Role', render: (val) => val || '-' },
              { key: 'shift', label: 'Shift', render: (val) => val || '-' },
              { key: 'total_sessions', label: 'Sessions', render: (val) => val?.toLocaleString() ?? '-' },
              { key: 'success_rate', label: 'Success Rate', render: (val) => (
                val != null ? (
                  <span className={`font-medium ${val >= 80 ? 'text-emerald-500' : val >= 60 ? 'text-yellow-500' : 'text-red-500'}`}>
                    {val}%
                  </span>
                ) : '-'
              )},
              { key: 'error_rate', label: 'Error Rate', render: (val) => (
                val != null ? (
                  <span className={`font-medium ${val <= 20 ? 'text-emerald-500' : val <= 40 ? 'text-yellow-500' : 'text-red-500'}`}>
                    {val}%
                  </span>
                ) : '-'
              )},
              { key: 'robots_used', label: 'Robots Used', render: (val) => val ?? '-' },
              { key: 'sessions_per_day', label: 'Sessions/Day', render: (val) => val ?? '-' }
            ]}
          />
        </>
      )}

      {/* Ward/Bed Analysis Tab */}
      {activeTab === 'wards' && (
        <>
          {/* Coverage KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <KPICard title="Total Wards" value={wardBed.coverage?.total_wards?.toLocaleString() || 0} subtitle={`${wardBed.coverage?.wards_with_sessions || 0} with sessions`} color="primary" />
            <KPICard title="Ward Coverage" value={`${wardBed.coverage?.ward_coverage_pct || 0}%`} subtitle="Wards with activity" color="success" />
            <KPICard title="Total Beds" value={wardBed.coverage?.total_beds?.toLocaleString() || 0} subtitle={`${wardBed.coverage?.beds_with_sessions || 0} with sessions`} color="warning" />
            <KPICard title="Bed Coverage" value={`${wardBed.coverage?.bed_coverage_pct || 0}%`} subtitle="Beds with activity" color="primary" />
          </div>


          {/* Ward Details Table */}
          <div className="mb-6">
            <SortableTable
              title="Ward Performance Details"
              data={wardBed.wards}
              defaultSortKey="total_sessions"
              onExport={() => exportToCSV(wardBed.wards, 'ward_performance_details')}
              columns={[
                { key: 'ward_name', label: 'Ward', render: (val) => val || '-' },
                { key: 'hospital_name', label: 'Hospital', render: (val, row) => val || row.hospital || '-' },
                { key: 'ward_type', label: 'Type', render: (val) => val || '-' },
                { key: 'beds_serviced', label: 'Beds', render: (val, row) => val ?? row.total_beds ?? '-' },
                { key: 'total_sessions', label: 'Sessions', render: (val) => val?.toLocaleString() ?? '-' },
                { key: 'success_rate', label: 'Success Rate', render: (val) => (
                  val != null ? (
                    <span className={`font-medium ${val >= 80 ? 'text-emerald-500' : val >= 60 ? 'text-yellow-500' : 'text-red-500'}`}>
                      {val}%
                    </span>
                  ) : '-'
                )},
                { key: 'robots_count', label: 'Robots', render: (val, row) => val ?? row.robots_used ?? '-' },
                { key: 'sessions_per_bed', label: 'Sessions/Bed', render: (val) => val ?? '-' }
              ]}
            />
          </div>

          {/* Sluice vs Beds Analysis */}
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden mb-6">
            <div className="p-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Sluice vs Beds Analysis</h3>
              <p className="text-sm text-gray-500">Compare disinfection activity between sluice rooms and patient beds</p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-4">
              {/* Sluice vs Beds Comparison Chart */}
              <div>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={wardBed.sluice_vs_beds || []} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" horizontal={true} vertical={false} />
                    <XAxis type="number" stroke="#9ca3af" tick={{ fontSize: 11 }} />
                    <YAxis dataKey="space_type" type="category" stroke="#9ca3af" tick={{ fontSize: 11 }} width={80} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="total_sessions" name="Total Sessions" fill={COLORS.primary} radius={[0, 4, 4, 0]} />
                    <Bar dataKey="successful" name="Successful" fill={COLORS.success} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              
              {/* Sluice vs Beds KPIs */}
              <div className="grid grid-cols-2 gap-4">
                {wardBed.sluice_vs_beds?.map((item, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${item.space_type === 'Sluice' ? 'bg-purple-50 border-purple-200' : 'bg-blue-50 border-blue-200'}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-lg ${item.space_type === 'Sluice' ? 'bg-purple-100' : 'bg-blue-100'}`}>
                        {item.space_type === 'Sluice' ? '🚿' : '🛏️'}
                      </span>
                      <h4 className={`text-lg font-semibold ${item.space_type === 'Sluice' ? 'text-purple-800' : 'text-blue-800'}`}>{item.space_type}</h4>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Sessions:</span>
                        <span className="font-bold text-gray-900">{item.total_sessions?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Success Rate:</span>
                        <span className={`font-bold ${item.success_rate >= 80 ? 'text-emerald-600' : item.success_rate >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>{item.success_rate}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Unique Spaces:</span>
                        <span className="font-bold text-gray-900">{item.unique_spaces?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Avg Duration:</span>
                        <span className="font-bold text-gray-900">{item.avg_duration} min</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Total Hours:</span>
                        <span className="font-bold text-gray-900">{item.total_hours}h</span>
                      </div>
                    </div>
                  </div>
                )) || <p className="text-gray-500 text-sm col-span-2">No sluice vs beds data available</p>}
              </div>
            </div>
          </div>

          {/* Sluice Activity by Ward Table */}
          <div className="mb-6">
            <SortableTable
              title="Sluice Activity by Ward"
              data={wardBed.sluice_by_ward || []}
              defaultSortKey="sluice_sessions"
              onExport={() => exportToCSV(wardBed.sluice_by_ward || [], 'sluice_by_ward')}
              columns={[
                { key: 'ward_name', label: 'Ward', render: (val) => val || '-' },
                { key: 'hospital_name', label: 'Hospital', render: (val) => val || '-' },
                { key: 'sluice_sessions', label: 'Sluice Sessions', render: (val) => val?.toLocaleString() ?? '-' },
                { key: 'sluice_count', label: 'Sluice Rooms', render: (val) => val ?? '-' },
                { key: 'success_rate', label: 'Success Rate', render: (val) => (
                  val != null ? (
                    <span className={`font-medium ${val >= 80 ? 'text-emerald-500' : val >= 60 ? 'text-yellow-500' : 'text-red-500'}`}>
                      {val}%
                    </span>
                  ) : '-'
                )},
                { key: 'avg_duration', label: 'Avg Duration', render: (val) => val != null ? `${val} min` : '-' }
              ]}
            />
          </div>

          {/* Bed Details Table */}
          <SortableTable
            title="Top Beds by Usage"
            data={wardBed.beds}
            defaultSortKey="total_sessions"
            onExport={() => exportToCSV(wardBed.beds, 'bed_usage_details')}
            columns={[
              { key: 'bed_name', label: 'Bed/Space', render: (val) => val || '-' },
              { key: 'section', label: 'Section', render: (val) => val || '-' },
              { key: 'ward', label: 'Ward', render: (val) => val || '-' },
              { key: 'hospital', label: 'Hospital', render: (val) => val || '-' },
              { key: 'total_sessions', label: 'Sessions', render: (val) => val?.toLocaleString() ?? '-' },
              { key: 'success_rate', label: 'Success Rate', render: (val) => (
                val != null ? (
                  <span className={`font-medium ${val >= 80 ? 'text-emerald-500' : val >= 60 ? 'text-yellow-500' : 'text-red-500'}`}>
                    {val}%
                  </span>
                ) : '-'
              )},
              { key: 'robots_used', label: 'Robots Used', render: (val) => Array.isArray(val) ? val.join(', ') : (val || '-') }
            ]}
          />
        </>
      )}

      {/* Compliance & Risk Tab */}
      {activeTab === 'compliance' && (
        <>
          {/* Top KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <KPICard
              title="Compliance Rate"
              value={`${performanceKPIs.kpis?.compliance_rate || 0}%`}
              subtitle="Double-session protocol"
              icon="success"
              color="success"
              tooltip="Percentage of beds that received the double-session disinfection protocol. Target: 80%+"
            />
            <KPICard
              title="24h Coverage"
              value={`${performanceKPIs.kpis?.coverage_rate || 0}%`}
              subtitle="Beds serviced in last 24h"
              icon="coverage"
              color="primary"
              tooltip="Percentage of all beds that received at least one disinfection session in the last 24 hours."
            />
            <KPICard
              title="High-Risk Operators"
              value={performanceKPIs.risk_indicators?.high_error_operators?.length || 0}
              subtitle="Error rate > 25%"
              icon="error"
              color="danger"
              tooltip="Operators with 10+ sessions and an error rate above 25%. These may require additional training."
            />
            <KPICard
              title="High-Risk Robots"
              value={performanceKPIs.risk_indicators?.high_error_robots?.length || 0}
              subtitle="Error rate > 25%"
              icon="robot"
              color="warning"
              tooltip="Robots with 10+ sessions and an error rate above 25%. These may require maintenance."
            />
          </div>

          {/* Compliance charts row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Bed Protocol Compliance */}
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h3 className="text-base font-semibold text-slate-800 mb-4">Bed Session Protocol Distribution</h3>
              {(() => {
                const c = performanceKPIs.compliance || {};
                const single = c.single_session_beds || 0;
                const double_ = c.double_session_beds || 0;
                const over = c.over_session_beds || 0;
                const total = single + double_ + over || 1;
                const bars = [
                  { label: 'Single Session', value: single, color: COLORS.warning, bg: 'bg-amber-100', text: 'text-amber-700' },
                  { label: 'Double Session (Protocol)', value: double_, color: COLORS.success, bg: 'bg-emerald-100', text: 'text-emerald-700' },
                  { label: 'Over-Protocol (3+)', value: over, color: COLORS.primary, bg: 'bg-blue-100', text: 'text-blue-700' },
                ];
                return (
                  <div className="space-y-4">
                    {bars.map(b => (
                      <div key={b.label}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-slate-600 font-medium">{b.label}</span>
                          <span className={`font-bold ${b.text}`}>{b.value.toLocaleString()} beds ({Math.round(b.value / total * 100)}%)</span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-3">
                          <div
                            className="h-3 rounded-full transition-all"
                            style={{ width: `${Math.round(b.value / total * 100)}%`, backgroundColor: b.color }}
                          />
                        </div>
                      </div>
                    ))}
                    <p className="text-xs text-slate-400 pt-1">Total active beds in period: {total.toLocaleString()}</p>
                  </div>
                );
              })()}
            </div>

            {/* Compliance Gap Analysis */}
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h3 className="text-base font-semibold text-slate-800 mb-4">Bed Coverage Gap Analysis</h3>
              {(() => {
                const g = performanceKPIs.compliance_gaps || {};
                const buckets = [
                  { label: 'Serviced < 24h', value: g.beds_serviced_24h || 0, color: COLORS.success, text: 'text-emerald-700' },
                  { label: 'Serviced 24–48h', value: g.beds_serviced_24_48h || 0, color: COLORS.warning, text: 'text-amber-700' },
                  { label: 'Serviced 48–72h', value: g.beds_serviced_48_72h || 0, color: '#f97316', text: 'text-orange-700' },
                  { label: 'Not serviced > 72h', value: g.beds_not_serviced_72h || 0, color: COLORS.danger, text: 'text-red-700' },
                ];
                const total = buckets.reduce((s, b) => s + b.value, 0) || 1;
                return (
                  <div className="space-y-4">
                    {buckets.map(b => (
                      <div key={b.label}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-slate-600 font-medium">{b.label}</span>
                          <span className={`font-bold ${b.text}`}>{b.value.toLocaleString()} beds ({Math.round(b.value / total * 100)}%)</span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-3">
                          <div
                            className="h-3 rounded-full transition-all"
                            style={{ width: `${Math.round(b.value / total * 100)}%`, backgroundColor: b.color }}
                          />
                        </div>
                      </div>
                    ))}
                    <p className="text-xs text-slate-400 pt-1">Total tracked beds: {total.toLocaleString()}</p>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Risk Indicators */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* High-error operators */}
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="flex items-center gap-3 p-5 border-b border-gray-100">
                <div className="w-9 h-9 bg-red-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-slate-800">High-Risk Operators</h3>
                  <p className="text-xs text-slate-400">10+ sessions with error rate &gt; 25%</p>
                </div>
              </div>
              {performanceKPIs.risk_indicators?.high_error_operators?.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-slate-500 text-xs uppercase">
                      <th className="text-left px-5 py-3 font-medium">Operator</th>
                      <th className="text-right px-5 py-3 font-medium">Sessions</th>
                      <th className="text-right px-5 py-3 font-medium">Errors</th>
                      <th className="text-right px-5 py-3 font-medium">Error Rate</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {performanceKPIs.risk_indicators.high_error_operators.map((op, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="px-5 py-3 font-medium text-slate-800">{op.name}</td>
                        <td className="px-5 py-3 text-right text-slate-600">{op.total_sessions}</td>
                        <td className="px-5 py-3 text-right text-slate-600">{op.errors}</td>
                        <td className="px-5 py-3 text-right">
                          <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">{op.error_rate}%</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-3">
                    <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-slate-700">No high-risk operators</p>
                  <p className="text-xs text-slate-400 mt-1">All operators are within acceptable error thresholds</p>
                </div>
              )}
            </div>

            {/* High-error robots */}
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="flex items-center gap-3 p-5 border-b border-gray-100">
                <div className="w-9 h-9 bg-amber-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h16a2 2 0 012 2v10a2 2 0 01-2 2h-2" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-slate-800">High-Risk Robots</h3>
                  <p className="text-xs text-slate-400">10+ sessions with error rate &gt; 25%</p>
                </div>
              </div>
              {performanceKPIs.risk_indicators?.high_error_robots?.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-slate-500 text-xs uppercase">
                      <th className="text-left px-5 py-3 font-medium">Robot</th>
                      <th className="text-right px-5 py-3 font-medium">Sessions</th>
                      <th className="text-right px-5 py-3 font-medium">Errors</th>
                      <th className="text-right px-5 py-3 font-medium">Error Rate</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {performanceKPIs.risk_indicators.high_error_robots.map((robot, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="px-5 py-3 font-medium text-slate-800 font-mono text-xs">{robot.serial}</td>
                        <td className="px-5 py-3 text-right text-slate-600">{robot.total_sessions}</td>
                        <td className="px-5 py-3 text-right text-slate-600">{robot.errors}</td>
                        <td className="px-5 py-3 text-right">
                          <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700">{robot.error_rate}%</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-3">
                    <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-slate-700">No high-risk robots</p>
                  <p className="text-xs text-slate-400 mt-1">All robots are within acceptable error thresholds</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Emergency Stop / Killswitch KPIs Tab */}
      {activeTab === 'killswitch' && (
        <>
          {/* Top KPI cards for emergency stops */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <KPICard
              title={`Emergency Stops (${customDateRange.start && customDateRange.end ? `${Math.ceil((new Date(customDateRange.end) - new Date(customDateRange.start)) / (1000 * 60 * 60 * 24)) + 1}d` : `${timeRange}d`})`}
              value={killswitchKPIs.period_stops || 0}
              subtitle={`${killswitchKPIs.period_days} day period`}
              icon="error"
              color="danger"
              tooltip="Total emergency stop activations in the current period"
            />
            <KPICard
              title="Emergency Stop Rate"
              value={`${killswitchKPIs.emergency_stop_rate || 0}%`}
              subtitle="of all sessions"
              icon="warning"
              color="warning"
              tooltip="Percentage of sessions interrupted by emergency stop activation"
            />
            <KPICard
              title="Affected Robots"
              value={killswitchKPIs.affected_robots || 0}
              subtitle={`in last ${customDateRange.start && customDateRange.end ? Math.ceil((new Date(customDateRange.end) - new Date(customDateRange.start)) / (1000 * 60 * 60 * 24)) + 1 : timeRange} days`}
              icon="robot"
              color="primary"
              tooltip="Number of unique robots that had emergency stops activated"
            />
            <KPICard
              title="All-Time Stops"
              value={killswitchKPIs.total_all_time || 0}
              subtitle="fleet history"
              icon="compliance"
              color="default"
              tooltip="Total emergency stop events across entire fleet history"
            />
          </div>

          {/* Emergency stops trend chart */}
          <div className="bg-white rounded-xl border border-gray-100 p-5 mb-6">
            <h3 className="text-base font-semibold text-slate-800 mb-4">Emergency Stop Trend (Last {customDateRange.start && customDateRange.end ? Math.ceil((new Date(customDateRange.end) - new Date(customDateRange.start)) / (1000 * 60 * 60 * 24)) + 1 : timeRange} Days)</h3>
            {killswitchKPIs.daily_trend && killswitchKPIs.daily_trend.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={killswitchKPIs.daily_trend}>
                  <defs>
                    <linearGradient id="killswitchGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#dc2626" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#dc2626" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" vertical={false} />
                  <XAxis dataKey="date" stroke="#9ca3af" tick={{ fontSize: 10 }} />
                  <YAxis stroke="#9ca3af" tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="stop_count" name="Emergency Stops" stroke="#dc2626" fill="url(#killswitchGradient)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center py-12 text-gray-400">
                <p>No emergency stop data available for this period</p>
              </div>
            )}
          </div>

          {/* Top operators and robots grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Top operators - most emergency stops */}
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="flex items-center gap-3 p-5 border-b border-gray-100">
                <div className="w-9 h-9 bg-red-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5-4a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-slate-800">Top Emergency Stop Operators</h3>
                  <p className="text-xs text-slate-400">Last {customDateRange.start && customDateRange.end ? Math.ceil((new Date(customDateRange.end) - new Date(customDateRange.start)) / (1000 * 60 * 60 * 24)) + 1 : timeRange} days</p>
                </div>
              </div>
              {killswitchKPIs.top_operators && killswitchKPIs.top_operators.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-slate-500 text-xs uppercase">
                      <th className="text-left px-5 py-3 font-medium">Operator</th>
                      <th className="text-right px-5 py-3 font-medium">Stops</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {killswitchKPIs.top_operators.slice(0, 10).map((op, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="px-5 py-3 font-medium text-slate-800">{op.operator_name || `Operator ${op.operator_id}`}</td>
                        <td className="px-5 py-3 text-right">
                          <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">{op.stop_count}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-3">
                    <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-slate-700">No data</p>
                  <p className="text-xs text-slate-400 mt-1">No emergency stops recorded in this period</p>
                </div>
              )}
            </div>

            {/* Top affected robots */}
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="flex items-center gap-3 p-5 border-b border-gray-100">
                <div className="w-9 h-9 bg-orange-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h16a2 2 0 012 2v10a2 2 0 01-2 2h-2" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-slate-800">Most Affected Robots</h3>
                  <p className="text-xs text-slate-400">Last {customDateRange.start && customDateRange.end ? Math.ceil((new Date(customDateRange.end) - new Date(customDateRange.start)) / (1000 * 60 * 60 * 24)) + 1 : timeRange} days</p>
                </div>
              </div>
              {killswitchKPIs.top_robots && killswitchKPIs.top_robots.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-slate-500 text-xs uppercase">
                      <th className="text-left px-5 py-3 font-medium">Robot Serial</th>
                      <th className="text-right px-5 py-3 font-medium">Stops</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {killswitchKPIs.top_robots.slice(0, 10).map((robot, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="px-5 py-3 font-medium text-slate-800 font-mono text-xs">{robot.robot_serial}</td>
                        <td className="px-5 py-3 text-right">
                          <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-orange-100 text-orange-700">{robot.stop_count}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-3">
                    <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-slate-700">No data</p>
                  <p className="text-xs text-slate-400 mt-1">No emergency stops recorded in this period</p>
                </div>
              )}
            </div>
          </div>

          {/* Emergency Stops by Hospital - Hospital Analysis */}
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="flex items-center gap-3 p-5 border-b border-gray-100">
              <div className="w-9 h-9 bg-red-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-800">Emergency Stops by Hospital</h3>
                <p className="text-xs text-slate-400">Click any hospital to view detailed emergency stop sessions</p>
              </div>
            </div>
            {killswitchKPIs.by_hospital && killswitchKPIs.by_hospital.length > 0 ? (
              <SortableTable
                data={killswitchKPIs.by_hospital}
                defaultSortKey="stop_count"
                onRowClick={loadEmergencyStopSessions}
                onExport={() => exportToCSV(killswitchKPIs.by_hospital, 'emergency_stops_by_hospital')}
                columns={[
                  { key: 'hospital_name', label: 'Hospital' },
                  { key: 'stop_count', label: 'Emergency Stops', render: (val) => (
                    <span className="font-semibold text-red-600">{val}</span>
                  )},
                  { key: 'affected_robots', label: 'Affected Robots', render: (val) => (
                    <span className="text-slate-700">{val}</span>
                  )},
                  { key: 'affected_operators', label: 'Operators Involved', render: (val) => (
                    <span className="text-slate-700">{val}</span>
                  )},
                  { key: 'locations_count', label: 'Unique Locations', render: (val) => (
                    <span className="text-slate-600">{val}</span>
                  )}
                ]}
              />
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-3">
                  <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-sm font-medium text-slate-700">No data available</p>
                <p className="text-xs text-slate-400 mt-1">No emergency stops recorded in this period</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Emergency Stop Sessions Modal */}
      {showEmergencyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-red-600 to-red-800 text-white px-6 py-4 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold">Emergency Stop Sessions Report</h2>
                {selectedEmergencyHospital && (
                  <p className="text-sm text-red-100 mt-1">
                    Hospital: {selectedEmergencyHospital.hospital_name} | Total Emergency Stops: {selectedEmergencyHospital.stop_count}
                  </p>
                )}
              </div>
              <button
                onClick={() => setShowEmergencyModal(false)}
                className="text-white hover:text-red-100 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-auto p-6">
              {emergencyStopSessions.length > 0 ? (
                <SortableTable
                  data={emergencyStopSessions}
                  defaultSortKey="MachineStartTime"
                  defaultSortDir="desc"
                  onExport={() => exportToCSV(emergencyStopSessions, `emergency_stops_${selectedEmergencyHospital?.hospital_name || 'export'}`)}
                  columns={[
                    { key: 'MachineStartTime', label: 'Timestamp', render: (val) => new Date(val).toLocaleString() },
                    { key: 'Serial', label: 'Robot', render: (val) => (
                      <span className="font-mono text-xs font-medium text-slate-800">{val}</span>
                    )},
                    { key: 'Operator', label: 'Operator', render: (val) => val || 'Unknown' },
                    { key: 'WardName', label: 'Ward', render: (val) => val || '-' },
                    { key: 'BedName', label: 'Location', render: (val) => val || '-' },
                    { key: 'TimeMinutes', label: 'Duration', render: (val) => val || '0 min' },
                    { key: 'Code', label: 'Status', render: (val) => (
                      <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                        {val}
                      </span>
                    )}
                  ]}
                />
              ) : (
                <div className="text-center py-12 text-gray-400">
                  <div className="text-4xl mb-2">🚨</div>
                  <p className="font-medium">No emergency stop sessions found</p>
                  <p className="text-sm">This hospital has no recorded emergency stops in the selected period</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end border-t border-gray-200">
              <button
                onClick={() => setShowEmergencyModal(false)}
                className="px-6 py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-lg font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trends & Patterns Tab */}
      {activeTab === 'trends' && (
        <>
          {/* Week-over-Week Comparison */}
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden mb-6">
            <div className="p-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Week-over-Week Performance</h3>
              <p className="text-sm text-gray-500">Compare this week's performance against last week</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4">
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-lg p-5 border border-blue-100">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-semibold text-blue-800 uppercase tracking-wider">This Week</h4>
                  <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                    (performanceKPIs.week_over_week?.sessions_change_percent || 0) >= 0 
                      ? 'bg-emerald-100 text-emerald-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {(performanceKPIs.week_over_week?.sessions_change_percent || 0) >= 0 ? '+' : ''}{performanceKPIs.week_over_week?.sessions_change_percent || 0}%
                  </span>
                </div>
                <p className="text-4xl font-bold text-blue-900 mb-1">{(performanceKPIs.week_over_week?.this_week_sessions || 0).toLocaleString()}</p>
                <p className="text-sm text-blue-600">sessions completed</p>
                <div className="mt-3 pt-3 border-t border-blue-100">
                  <p className="text-sm text-gray-600">Success Rate: <span className="font-semibold text-emerald-600">{performanceKPIs.week_over_week?.this_week_success_rate || 0}%</span></p>
                </div>
              </div>
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-lg p-5 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-semibold text-gray-600 uppercase tracking-wider">Last Week</h4>
                </div>
                <p className="text-4xl font-bold text-gray-700 mb-1">{(performanceKPIs.week_over_week?.last_week_sessions || 0).toLocaleString()}</p>
                <p className="text-sm text-gray-500">sessions completed</p>
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-sm text-gray-600">Success Rate: <span className="font-semibold text-gray-700">{performanceKPIs.week_over_week?.last_week_success_rate || 0}%</span></p>
                </div>
              </div>
            </div>
          </div>

          {/* Weekly & Monthly Performance Trends */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Weekly Trend */}
            <ChartCard
              id="weekly-trend-tab"
              title="Weekly Performance Trend (Last 8 Weeks)"
              data={performanceKPIs.trends?.weekly}
              onDrillDown={() => setDrillDown({ data: performanceKPIs.trends?.weekly, title: 'Weekly Performance Data' })}
            >
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={performanceKPIs.trends?.weekly || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" vertical={false} />
                  <XAxis dataKey="week" stroke="#9ca3af" tick={{ fontSize: 10 }} />
                  <YAxis yAxisId="left" stroke="#9ca3af" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="right" orientation="right" stroke="#9ca3af" tick={{ fontSize: 11 }} domain={[0, 100]} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="sessions" name="Sessions" stroke={COLORS.primary} strokeWidth={2} dot={{ fill: COLORS.primary }} />
                  <Line yAxisId="right" type="monotone" dataKey="success_rate" name="Success Rate %" stroke={COLORS.success} strokeWidth={2} dot={{ fill: COLORS.success }} />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Monthly Trend */}
            <ChartCard
              id="monthly-trend-tab"
              title="Monthly Performance Trend (Last 6 Months)"
              data={performanceKPIs.trends?.monthly}
              onDrillDown={() => setDrillDown({ data: performanceKPIs.trends?.monthly, title: 'Monthly Performance Data' })}
            >
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={performanceKPIs.trends?.monthly || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" vertical={false} />
                  <XAxis dataKey="month" stroke="#9ca3af" tick={{ fontSize: 10 }} />
                  <YAxis yAxisId="left" stroke="#9ca3af" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="right" orientation="right" stroke="#9ca3af" tick={{ fontSize: 11 }} domain={[0, 100]} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar yAxisId="left" dataKey="sessions" name="Sessions" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
                  <Line yAxisId="right" type="monotone" dataKey="success_rate" name="Success Rate %" stroke={COLORS.success} strokeWidth={3} dot={{ fill: COLORS.success, strokeWidth: 2 }} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* Error Rate Trend */}
          <ChartCard
            id="error-rate-trend-tab"
            title="Error Rate Trend (Last 30 Days)"
            data={performanceKPIs.trends?.error_rate}
            onDrillDown={() => setDrillDown({ data: performanceKPIs.trends?.error_rate, title: 'Error Rate Trend Data' })}
          >
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={performanceKPIs.trends?.error_rate || []}>
                <defs>
                  <linearGradient id="colorErrorRateTrend" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.danger} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={COLORS.danger} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" vertical={false} />
                <XAxis dataKey="date" stroke="#9ca3af" tick={{ fontSize: 10 }} />
                <YAxis stroke="#9ca3af" tick={{ fontSize: 11 }} domain={[0, 'auto']} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="error_rate" name="Error Rate %" stroke={COLORS.danger} fill="url(#colorErrorRateTrend)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Hourly Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 mt-6">
            <ChartCard
              id="hourly-distribution-tab"
              title="Hourly Activity Distribution"
              data={hourly}
              onDrillDown={() => setDrillDown({ data: hourly, title: 'Hourly Activity Data' })}
            >
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={hourly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" vertical={false} />
                  <XAxis dataKey="label" stroke="#9ca3af" tick={{ fontSize: 10 }} />
                  <YAxis stroke="#9ca3af" tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="sessions" name="Sessions" fill={COLORS.accent} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Shift Performance */}
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="p-4 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900">Shift Performance Analysis</h3>
                <p className="text-sm text-gray-500">Compare day shift (6AM-6PM) vs night shift (6PM-6AM) efficiency</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
                {performanceKPIs.efficiency?.shift_performance?.map((shift, i) => (
                  <div key={i} className={`rounded-lg p-5 border ${shift.shift?.includes('Day') ? 'bg-amber-50 border-amber-200' : 'bg-indigo-50 border-indigo-200'}`}>
                    <div className="flex items-center gap-3 mb-4">
                      <span className={`w-10 h-10 rounded-full flex items-center justify-center text-lg ${shift.shift?.includes('Day') ? 'bg-amber-100' : 'bg-indigo-100'}`}>
                        {shift.shift?.includes('Day') ? '☀️' : '🌙'}
                      </span>
                      <h4 className={`text-lg font-semibold ${shift.shift?.includes('Day') ? 'text-amber-800' : 'text-indigo-800'}`}>{shift.shift}</h4>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-2xl font-bold text-gray-900">{(shift.sessions || 0).toLocaleString()}</p>
                        <p className="text-xs text-gray-500">Total Sessions</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-emerald-600">{shift.success_rate || 0}%</p>
                        <p className="text-xs text-gray-500">Success Rate</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-gray-700">{shift.operators || 0}</p>
                        <p className="text-xs text-gray-500">Active Operators</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-blue-600">{shift.sessions_per_operator || 0}</p>
                        <p className="text-xs text-gray-500">Sessions/Operator</p>
                      </div>
                    </div>
                  </div>
                )) || <p className="text-gray-500 text-sm col-span-2">No shift data available</p>}
              </div>
            </div>
          </div>

          {/* Weekly Performance Breakdown Table */}
          <div className="mb-6">
            <SortableTable
              title="Weekly Performance Breakdown"
              data={performanceKPIs.trends?.weekly || []}
              defaultSortKey="week"
              defaultSortDir="desc"
              onExport={() => exportToCSV(performanceKPIs.trends?.weekly || [], 'weekly_performance')}
              columns={[
                { key: 'week', label: 'Week' },
                { key: 'sessions', label: 'Sessions' },
                { key: 'successful', label: 'Successful', render: (val) => <span className="text-emerald-600">{val?.toLocaleString()}</span> },
                { key: 'failed', label: 'Failed', render: (val) => <span className="text-red-600">{val?.toLocaleString()}</span> },
                { key: 'success_rate', label: 'Success Rate', render: (val) => (
                  <span className={`font-medium ${val >= 80 ? 'text-emerald-600' : val >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                    {val}%
                  </span>
                )}
              ]}
            />
          </div>

          {/* Monthly Performance Breakdown Table */}
          <div className="mb-6">
            <SortableTable
              title="Monthly Performance Breakdown"
              data={performanceKPIs.trends?.monthly || []}
              defaultSortKey="month"
              defaultSortDir="desc"
              onExport={() => exportToCSV(performanceKPIs.trends?.monthly || [], 'monthly_performance')}
              columns={[
                { key: 'month', label: 'Month' },
                { key: 'sessions', label: 'Sessions' },
                { key: 'successful', label: 'Successful', render: (val) => <span className="text-emerald-600">{val?.toLocaleString()}</span> },
                { key: 'failed', label: 'Failed', render: (val) => <span className="text-red-600">{val?.toLocaleString()}</span> },
                { key: 'success_rate', label: 'Success Rate', render: (val) => (
                  <span className={`font-medium ${val >= 80 ? 'text-emerald-600' : val >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                    {val}%
                  </span>
                )}
              ]}
            />
          </div>
        </>
      )}

      {/* Additional Insights Tab */}
      {activeTab === 'insights' && (
        <>
          {/* Period Stats KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            <KPICard title="Unique Robots" value={additionalInsights.period_stats?.unique_robots || 0} subtitle="Active in period" icon="🤖" color="cyan" />
            <KPICard title="Unique Operators" value={additionalInsights.period_stats?.unique_operators || 0} subtitle="Active in period" icon="👤" color="emerald" />
            <KPICard title="Unique Beds" value={additionalInsights.period_stats?.unique_beds || 0} subtitle="Serviced in period" icon="🛏️" color="purple" />
            <KPICard title="Active Days" value={additionalInsights.period_stats?.active_days || 0} subtitle="Days with sessions" icon="📅" color="blue" />
            <KPICard title="Avg Sessions/Day" value={additionalInsights.period_stats?.avg_sessions_per_day || 0} subtitle="Daily average" icon="📊" color="pink" />
          </div>

          {/* Duration & Day of Week Analysis */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <ChartCard id="duration-analysis" title="Session Duration Analysis" data={Array.isArray(additionalInsights.duration_analysis) ? additionalInsights.duration_analysis.filter(d => ['0-5 min', '5-10 min', '10 min'].includes(d.category)) : []}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={Array.isArray(additionalInsights.duration_analysis) ? additionalInsights.duration_analysis.filter(d => ['0-5 min', '5-10 min', '10 min'].includes(d.category)) : []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" vertical={false} />
                  <XAxis dataKey="category" stroke="#9ca3af" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" height={60} />
                  <YAxis stroke="#9ca3af" tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Sessions" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard id="day-of-week" title="Day of Week Performance" data={Array.isArray(additionalInsights.day_of_week) ? additionalInsights.day_of_week : []}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={Array.isArray(additionalInsights.day_of_week) ? additionalInsights.day_of_week : []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" vertical={false} />
                  <XAxis dataKey="day" stroke="#9ca3af" tick={{ fontSize: 11 }} />
                  <YAxis stroke="#9ca3af" tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="sessions" name="Sessions" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="successful" name="Successful" fill={COLORS.success} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* Peak Activity Hours - Full 24-hour chart */}
          <ChartCard id="peak-hours" title="Peak Activity Hours (24-Hour Distribution)" data={Array.isArray(additionalInsights.peak_hours) ? additionalInsights.peak_hours : []}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={Array.isArray(additionalInsights.peak_hours) ? additionalInsights.peak_hours : []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" vertical={false} />
                <XAxis dataKey="hour" stroke="#9ca3af" tick={{ fontSize: 10 }} />
                <YAxis stroke="#9ca3af" tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="sessions" name="Sessions" fill={COLORS.accent} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Database Totals */}
          <div className="bg-white rounded-xl border border-gray-100 p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Database Overview (All Time)</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <div className="text-center">
                <p className="text-3xl font-bold text-cyan-400">{additionalInsights.database_totals?.total_sessions_all_time?.toLocaleString() || 0}</p>
                <p className="text-gray-500 text-sm">Total Sessions</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-emerald-400">{additionalInsights.database_totals?.total_robots || 0}</p>
                <p className="text-gray-500 text-sm">Total Robots</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-purple-400">{additionalInsights.database_totals?.total_operators || 0}</p>
                <p className="text-gray-500 text-sm">Total Operators</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-blue-400">{additionalInsights.database_totals?.total_beds?.toLocaleString() || 0}</p>
                <p className="text-gray-500 text-sm">Total Beds</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-pink-400">{additionalInsights.database_totals?.total_wards || 0}</p>
                <p className="text-gray-500 text-sm">Total Wards</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-yellow-400">{additionalInsights.database_totals?.total_hospitals || 0}</p>
                <p className="text-gray-500 text-sm">Total Hospitals</p>
              </div>
            </div>
            {additionalInsights.database_totals?.first_session && (
              <div className="mt-4 pt-4 border-t border-gray-200 text-center text-gray-500 text-sm">
                Data range: {new Date(additionalInsights.database_totals.first_session).toLocaleDateString()} - {new Date(additionalInsights.database_totals.last_session).toLocaleDateString()}
              </div>
            )}
          </div>

        </>
      )}
      </div>

      {/* Footer */}
      <div className="mt-8 text-center text-gray-400 text-sm">
        <p>EveSix Analytics Platform v2.5 | Data refreshed: {new Date().toLocaleString()}</p>
      </div>

      {/* Export Modal */}
      {showExportModal && <ExportModal onClose={() => setShowExportModal(false)} />}

      {/* UVC Hours Reset Modal (super admin only) */}
      {isSuperAdmin && showUvcResetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-6 py-4">
              <h3 className="text-xl font-bold">Reset UVC Hours</h3>
              <p className="text-sm text-amber-100 mt-1">
                Records a bulb replacement for one robot. Its UVC hours restart from the reset date; other robots are unaffected.
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Robot</label>
                <select
                  value={uvcResetForm.serial}
                  onChange={(e) => setUvcResetForm(prev => ({ ...prev, serial: e.target.value }))}
                  className="w-full bg-slate-50 text-slate-700 rounded-xl px-4 py-2.5 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                >
                  <option value="">Select a robot...</option>
                  {availableRobots.map((r, idx) => (
                    <option key={`uvc-reset-robot-${idx}-${r.serial}`} value={r.serial}>{r.serial}</option>
                  ))}
                </select>
                {availableRobots.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    No robots found for the current time range filter — widen the date range to see robots here.
                  </p>
                )}
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Reset Date</label>
                  <input
                    type="date"
                    value={uvcResetForm.reset_date}
                    onChange={(e) => setUvcResetForm(prev => ({ ...prev, reset_date: e.target.value }))}
                    className="w-full bg-slate-50 text-slate-700 rounded-xl px-4 py-2.5 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Reset Time</label>
                  <input
                    type="time"
                    value={uvcResetForm.reset_time}
                    onChange={(e) => setUvcResetForm(prev => ({ ...prev, reset_time: e.target.value }))}
                    className="w-full bg-slate-50 text-slate-700 rounded-xl px-4 py-2.5 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  />
                  <p className="text-xs text-slate-500 mt-1">Sessions before this time on the reset day still count toward the old bulbs.</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes (optional)</label>
                <textarea
                  value={uvcResetForm.notes}
                  onChange={(e) => setUvcResetForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="e.g. UVC bulbs replaced during maintenance"
                  rows={3}
                  className="w-full bg-slate-50 text-slate-700 rounded-xl px-4 py-2.5 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm resize-none"
                />
              </div>
            </div>
            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
              <button
                onClick={() => setShowUvcResetModal(false)}
                disabled={uvcResetSaving}
                className="px-5 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUvcReset}
                disabled={uvcResetSaving}
                className="px-5 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uvcResetSaving ? 'Saving...' : 'Confirm Reset'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fleet Sessions Modal */}
      {showFleetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white px-6 py-4 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold">Fleet Session Details</h2>
                {selectedFleetRobot && (
                  <p className="text-sm text-blue-100 mt-1">
                    Robot: {selectedFleetRobot.serial} | Hospital: {selectedFleetRobot.hospital} | Sessions: {selectedFleetRobot.total_sessions}
                  </p>
                )}
              </div>
              <button
                onClick={() => setShowFleetModal(false)}
                className="text-white hover:text-blue-100 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-auto p-6">
              {fleetSessions.length > 0 ? (
                <SortableTable
                  data={fleetSessions}
                  defaultSortKey="MachineStartTime"
                  defaultSortDir="desc"
                  onExport={(rows, cols) => exportToCSV(rows, `fleet_sessions_${selectedFleetRobot?.serial || 'export'}`, cols)}
                  columns={[
                    { key: 'Hospital_Name', label: 'Hospital' },
                    { key: 'Serial', label: 'Robot' },
                    { key: 'MachineStartTime', label: 'Session Start', render: (val) => val || '-' },
                    { key: 'CleaningCycleEndTime', label: 'Cycle End', render: (val) => val || '-' },
                    { key: 'TimeMinutes', label: 'Duration', render: (val) => {
                      // val contains the duration string with emoji
                      return <span>{val}</span>;
                    }},
                    { key: 'Code', label: 'Result' },
                    { key: 'WardName', label: 'Ward', render: (val) => val || '-' },
                    { key: 'BedName', label: 'Bed', render: (val) => val || '-' },
                    { key: 'Operator', label: 'Operator', render: (val) => val || '-' }
                  ]}
                />
              ) : (
                <div className="text-center py-12 text-gray-400">
                  <div className="text-4xl mb-2">📊</div>
                  <p className="font-medium">No session data available</p>
                  <p className="text-sm">Try adjusting your date range</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end border-t border-gray-200">
              <button
                onClick={() => setShowFleetModal(false)}
                className="px-6 py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-lg font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InsightsDashboard;
